import { useEffect, useRef, forwardRef, useImperativeHandle, useCallback } from "react";
import {
  playBumperSound,
  playPegSound,
  playDeflectorSound,
  playWallSound,
  playLaunchSound,
  playBasketSound,
  playRejectSound,
  playCompleteSound,
  playSpinnerSound,
} from "../utils/sounds";

interface PinballGameProps {
  /** Called when a ball comes to rest in a basket. */
  onBallSettled: (ballIndex: number, basketNumber: number) => void;
  /** Called once all balls have been settled. */
  onAllNumbersSelected: (numbers: number[]) => void;
  selectedNumbers: number[];
  isComplete: boolean;
  currentBallIndex: number;
  onLaunchEnd: () => void;
  isStarted: boolean;
}

const GAME_W = 560;
const GAME_H = 800;
const PLUNGER_AREA_W = 60;
const TOTAL_W = GAME_W + PLUNGER_AREA_W;
const BALL_R = 11;
const BALL_COUNT = 6;
const BASKET_COLS = 9;
const BASKET_ROWS = 5;
const RETRY_TIMEOUT = 9500; // 전체 타임아웃 9.5초
const BASKET_ENTRY_TIMEOUT = 6000; // 바구니 진입 후 6초
const PLUNGER_STUCK_TIMEOUT = 1500; // 플런저 구간 갇힘 타임아웃 1.5초

const BASKET_PIN_GAP = 18; // 바구니 상단 핀과 바구니 간의 간격

const BALL_COLORS = [
  { fill: "#fbbf24", stroke: "#b45309", glow: "rgba(251,191,36,0.4)", highlight: "#fef3c7" },
  { fill: "#f87171", stroke: "#b91c1c", glow: "rgba(248,113,113,0.4)", highlight: "#fecaca" },
  { fill: "#34d399", stroke: "#047857", glow: "rgba(52,211,153,0.4)", highlight: "#a7f3d0" },
  { fill: "#60a5fa", stroke: "#1d4ed8", glow: "rgba(96,165,250,0.4)", highlight: "#bfdbfe" },
  { fill: "#c084fc", stroke: "#7c3aed", glow: "rgba(192,132,252,0.4)", highlight: "#e9d5ff" },
  { fill: "#fb923c", stroke: "#c2410c", glow: "rgba(251,146,60,0.4)", highlight: "#fed7aa" },
];

// 랜덤 오프셋 생성 함수
function randOffset(range: number) {
  return (Math.random() - 0.5) * range;
}

const PinballGame = forwardRef(
  (
    {
      onBallSettled,
      onAllNumbersSelected,
      selectedNumbers,
      isComplete,
      currentBallIndex,
      onLaunchEnd,
      isStarted,
    }: PinballGameProps,
    ref
  ) => {
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const rafRef = useRef<number>(0);
    const engineRef = useRef<any>(null);
    const activeBallRef = useRef<any>(null);
    const settledRef = useRef<Map<any, number>>(new Map());
    const settledBallsRef = useRef<any[]>([]);
    const stableTimersRef = useRef<Map<any, number>>(new Map());
    const isCompleteRef = useRef(false);
    const currentBallIndexRef = useRef(0);
    const particlesRef = useRef<
      Array<{ x: number; y: number; vx: number; vy: number; life: number; color: string; size: number }>
    >([]);
    const bumperFlashRef = useRef<Map<string, number>>(new Map());
    const trailsRef = useRef<Map<any, Array<{ x: number; y: number; alpha: number }>>>(new Map());
    const basketSensorsRef = useRef<any[]>([]);
    const worldBodiesRef = useRef<any[]>([]);
    const runnerRef = useRef<{ enabled: boolean }>({ enabled: false });
    const bumpersRef = useRef<any[]>([]);
    const deflectorsRef = useRef<any[]>([]);
    const spinnersRef = useRef<any[]>([]);
    const spinnerAnglesRef = useRef<number[]>([]);
    const basketTopBumpersRef = useRef<any[]>([]);

    const gateWallRef = useRef<any>(null);
    const gateActiveRef = useRef(false);
    const ballLaunchTimeRef = useRef<number>(0);
    const retryTimeoutRef = useRef<number | null>(null);
    const basketEntryTimeRef = useRef<number>(0);
    const hasEnteredBasketRef = useRef<boolean>(false);
    // 목표 행 (0-indexed, 0=1행, 4=5행)
    const targetRowRef = useRef<number>(-1);

    const stuckTimerRef = useRef<number>(0);
    const lastBallPosRef = useRef<{ x: number; y: number } | null>(null);

    const plungerStuckTimerRef = useRef<number | null>(null);

    // 랜덤 범퍼 위치 저장 (idle 그리기용)
    const randomBumperPositionsRef = useRef<Array<{ x: number; y: number; r: number; type?: string }>>([]);
    const randomDeflectorPositionsRef = useRef<Array<{ x: number; y: number; angle: number; w: number }>>([]);
    const randomSpinnerPositionsRef = useRef<Array<{ x: number; y: number; w: number }>>([]);

    // 바구니 번호 랜덤 배치용
    const basketNumbersRef = useRef<number[]>([]);

    // BLAST OFF 효과용
    const blastOffRef = useRef<{ active: boolean; opacity: number; scale: number; phase: number; rocketY: number }>({
      active: false,
      opacity: 0,
      scale: 0.5,
      phase: 0,
      rocketY: GAME_H / 2,
    });

    // 화면 밖 이탈 처리 완료 플래그 (1번만 발동)
    const blastOffHandledRef = useRef(false);

    const goodEffectRef = useRef<{ active: boolean; opacity: number; x: number; y: number; phase: number }>({
      active: false,
      opacity: 0,
      x: 0,
      y: 0,
      phase: 0,
    });

    // 별 파티클 (화성갈끄야 전용)
    const starParticlesRef = useRef<Array<{
      angle: number; radius: number; orbitSpeed: number;
      life: number; size: number; rotation: number; rotSpeed: number; color: string;
      centerX: number; centerY: number;
    }>>([]);

    const plungerRef = useRef({
      isDragging: false,
      pullAmount: 0,
      baseY: 650,
      maxPull: 120,
      currentY: 650,
      springAnim: 0,
      canLaunch: true,
      ballWaiting: true,
    });

    const isBallFlyingRef = useRef(false);

    // Ref to hold prepareBallForLaunch so launchCurrentBall can call it without circular dep
    const prepareBallFnRef = useRef<() => void>(() => {});

    // keep refs up‑to‑date
    isCompleteRef.current = isComplete;
    currentBallIndexRef.current = currentBallIndex;

    // 랜덤 범퍼 위치 (10개 빨간 + 4개 파란) - 겹치지 않도록 완전히 재배치
    const generateRandomBumperPositions = useCallback(() => {
      // 모든 장애물 간 최소 거리를 보장하는 배치
      // 게임 영역: x(30~530), y(70~560)
      // 빨간 범퍼 r≈26, 파란 범퍼 r≈34
      // 디플렉터 위치: (130,300), (420,310), (100,450), (380,440) - w≈55
      // 스피너 위치: (280,150), (500,350), (80,200) - w≈55
      // 모든 범퍼는 위 장애물들과 최소 85px 이상 떨어져야 함
      const baseBumpers = [
        // 빨간 범퍼 10개 - 넓게 분산 배치
        { x: 110, y: 80, r: 26, type: "red" },
        { x: 250, y: 80, r: 28, type: "red" },
        { x: 430, y: 80, r: 26, type: "red" },

        { x: 170, y: 200, r: 25, type: "red" },
        { x: 370, y: 210, r: 25, type: "red" },

        { x: 260, y: 330, r: 26, type: "red" },
        { x: 50, y: 360, r: 24, type: "red" },
        { x: 500, y: 240, r: 26, type: "red" },

        { x: 200, y: 470, r: 25, type: "red" },
        { x: 350, y: 540, r: 25, type: "red" },

        // 파란 범퍼 4개 - 빨간/디플렉터/스피너와 모두 85px+ 떨어진 위치
        { x: 60, y: 160, r: 30, type: "blue" },
        { x: 65, y: 530, r: 34, type: "blue" },
        { x: 480, y: 460, r: 33, type: "blue" },
        { x: 300, y: 240, r: 33, type: "blue" },
      ];
      const positions = baseBumpers.map((b) => ({
        x: b.x + randOffset(4),
        y: b.y + randOffset(4),
        r: b.r + randOffset(1),
        type: b.type,
      }));
      randomBumperPositionsRef.current = positions;
      return positions;
    }, []);

    // 디플렉터 4개 - 범퍼/스피너와 겹치지 않는 위치
    const generateRandomDeflectorPositions = useCallback(() => {
      // 범퍼/스피너와 최소 85px 이상 떨어진 위치
      const baseDeflectors = [
        { x: 130, y: 300, angle: 0.35, w: 80 },
        { x: 420, y: 340, angle: -0.35, w: 80 },
        { x: 100, y: 450, angle: 0.3, w: 80 },
        { x: 380, y: 440, angle: -0.3, w: 80 },
      ];
      const positions = baseDeflectors.map((d) => ({
        x: d.x + randOffset(4),
        y: d.y + randOffset(4),
        angle: d.angle + randOffset(0.04),
        w: d.w + randOffset(3),
      }));
      randomDeflectorPositionsRef.current = positions;
      return positions;
    }, []);

    // 스핀너 3개 - 다른 모든 장애물과 겹치지 않는 위치
    const generateRandomSpinnerPositions = useCallback(() => {
      // 범퍼/디플렉터와 최소 85px 이상 떨어진 위치
      const baseSpinners = [
        { x: 280, y: 150, w: 50 },
        { x: 520, y: 310, w: 50 },
        { x: 280, y: 490, w: 50 },
      ];
      const positions = baseSpinners.map((s) => ({
        x: s.x + randOffset(4),
        y: s.y + randOffset(4),
        w: s.w + randOffset(3),
      }));
      randomSpinnerPositionsRef.current = positions;
      spinnerAnglesRef.current = positions.map(() => Math.random() * Math.PI);
      return positions;
    }, []);

    // 바구니 번호 셔플 함수
    const shuffleBasketNumbers = useCallback(() => {
      const nums = Array.from({ length: 45 }, (_, i) => i + 1);
      for (let i = nums.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [nums[i], nums[j]] = [nums[j], nums[i]];
      }
      basketNumbersRef.current = nums;
      return nums;
    }, []);

    // 안착 번호 표시 효과
    const settledNumberDisplayRef = useRef<{
      active: boolean;
      number: number;
      opacity: number;
      scale: number;
      phase: number;
      ballIdx: number;
    }>({
      active: false,
      number: 0,
      opacity: 0,
      scale: 0.5,
      phase: 0,
      ballIdx: 0,
    });

    const cleanupPhysics = useCallback(() => {
      const Matter = (window as any).Matter;
      if (engineRef.current && Matter) {
        Matter.Engine.clear(engineRef.current);
      }
      runnerRef.current.enabled = false;
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }
    }, []);

    const spawnParticles = useCallback(
      (x: number, y: number, count: number, colors: string[], sizeBase = 3) => {
        for (let i = 0; i < count; i++) {
          const angle = (Math.PI * 2 * i) / count + Math.random() * 0.5;
          const speed = 1.5 + Math.random() * 4;
          particlesRef.current.push({
            x,
            y,
            vx: Math.cos(angle) * speed,
            vy: Math.sin(angle) * speed,
            life: 1,
            color: colors[Math.floor(Math.random() * colors.length)],
            size: sizeBase + Math.random() * 2,
          });
        }
      },
      []
    );

    const prepareBallForLaunch = useCallback(() => {
      const Matter = (window as any).Matter;
      if (!Matter || !engineRef.current) return;
      const { Bodies, World } = Matter;

      const bIdx = currentBallIndexRef.current;
      if (bIdx >= BALL_COUNT) return;

      if (gateWallRef.current) {
        World.remove(engineRef.current.world, gateWallRef.current);
        gateWallRef.current = null;
        gateActiveRef.current = false;
      }

      const plungerX = GAME_W + PLUNGER_AREA_W / 2;
      const plungerBallY = plungerRef.current.baseY - 80 + randOffset(10);

      const ball = Bodies.circle(plungerX, plungerBallY, BALL_R, {
        // 공마다 물리 속성을 더 넓은 범위로 랜덤화
        restitution: 0.45 + Math.random() * 0.4,
        friction: 0.005 + Math.random() * 0.06,
        density: 0.0015 + Math.random() * 0.004,
        label: `ball-${bIdx}`,
        isStatic: true,
      });

      World.add(engineRef.current.world, [ball]);
      activeBallRef.current = ball;
      trailsRef.current.set(ball, []);
      isBallFlyingRef.current = false;
      plungerRef.current.canLaunch = true;
      plungerRef.current.ballWaiting = true;
      plungerRef.current.pullAmount = 0;
      plungerRef.current.currentY = plungerRef.current.baseY;
      ballLaunchTimeRef.current = 0;
      basketEntryTimeRef.current = 0;
      hasEnteredBasketRef.current = false;
      targetRowRef.current = -1;
      blastOffHandledRef.current = false;
      stuckTimerRef.current = 0;
      lastBallPosRef.current = null;

      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
        retryTimeoutRef.current = null;
      }
    }, []);

    // Keep the ref in sync
    prepareBallFnRef.current = prepareBallForLaunch;

    const launchCurrentBall = useCallback(() => {
      const Matter = (window as any).Matter;
      if (!Matter || !activeBallRef.current) return;
      const { Body } = Matter;
      const ball = activeBallRef.current;
      const pull = plungerRef.current.pullAmount;

      if (pull < 0.02) return;

      Body.setStatic(ball, false);

      // 🅐 forceY: pull 세기에 비례한 높이 편차
      const forceY = -(0.035 + pull * 0.16) + randOffset(0.004 + pull * 0.012);
      // 🅑 forceX: pull 세기에 비례한 좌우 편차
      const forceX = -(0.005 + pull * 0.01) + randOffset(0.008 + pull * 0.02);
      Body.applyForce(ball, ball.position, { x: forceX, y: forceY });

      // 🅒 angularVelocity: pull 세기에 비례한 회전 편차
      Body.setAngularVelocity(ball, randOffset(0.3 + pull * 1.0));

      const bIdx = currentBallIndexRef.current;
      const col = BALL_COLORS[bIdx] || BALL_COLORS[0];
      spawnParticles(ball.position.x, ball.position.y, 10, [col.fill, "#fff", "#fbbf24"], 3);

      playLaunchSound();

      isBallFlyingRef.current = true;
      plungerRef.current.canLaunch = false;
      plungerRef.current.ballWaiting = false;
      plungerRef.current.springAnim = 1;
      ballLaunchTimeRef.current = Date.now();
      basketEntryTimeRef.current = 0;
      hasEnteredBasketRef.current = false;

      // 플런저 구간 갇힘 타임아웃 (1.5초)
      if (plungerStuckTimerRef.current) {
        clearTimeout(plungerStuckTimerRef.current);
      }
      plungerStuckTimerRef.current = window.setTimeout(() => {
        if (
          isBallFlyingRef.current &&
          activeBallRef.current &&
          !settledRef.current.has(activeBallRef.current) &&
          activeBallRef.current.position.x >= GAME_W - BALL_R
        ) {
          const Matter2 = (window as any).Matter;
          if (Matter2 && engineRef.current) {
            const { World } = Matter2;
            const oldBall = activeBallRef.current;
            World.remove(engineRef.current.world, oldBall);
            trailsRef.current.delete(oldBall);
            activeBallRef.current = null;
            isBallFlyingRef.current = false;

            if (gateWallRef.current) {
              World.remove(engineRef.current.world, gateWallRef.current);
              gateWallRef.current = null;
              gateActiveRef.current = false;
            }

            if (retryTimeoutRef.current) {
              clearTimeout(retryTimeoutRef.current);
              retryTimeoutRef.current = null;
            }

            setTimeout(() => {
              prepareBallFnRef.current();
            }, 100);
          }
        }
        plungerStuckTimerRef.current = null;
      }, PLUNGER_STUCK_TIMEOUT);

      // 전체 타임아웃 9.5초 (바구니에 아예 못 들어오는 경우 대비)
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }

      const retryLaunch = () => {
        if (
          isBallFlyingRef.current &&
          activeBallRef.current &&
          !settledRef.current.has(activeBallRef.current)
        ) {
          const Matter2 = (window as any).Matter;
          if (Matter2 && engineRef.current) {
            const { World } = Matter2;
            const oldBall = activeBallRef.current;

            // 기존 공 제거
            World.remove(engineRef.current.world, oldBall);
            trailsRef.current.delete(oldBall);
            activeBallRef.current = null;
            isBallFlyingRef.current = false;

            // 게이트 제거 (플런저 레인 다시 열기)
            if (gateWallRef.current) {
              World.remove(engineRef.current.world, gateWallRef.current);
              gateWallRef.current = null;
              gateActiveRef.current = false;
            }

            // 100ms 후 새 공을 플런저에 올려놓기 (유저가 직접 당겨서 발사)
            setTimeout(() => {
              prepareBallFnRef.current();
            }, 100);
          }
        }
      };

      retryTimeoutRef.current = window.setTimeout(retryLaunch, RETRY_TIMEOUT);
    }, [spawnParticles]);

    const initPhysicsWorld = useCallback(() => {
      const Matter = (window as any).Matter;
      if (!Matter || !canvasRef.current) return;

      cleanupPhysics();

      const { Engine, World, Bodies, Events } = Matter;
      const engine = Engine.create({ gravity: { x: 0, y: 1.0 } });
      engineRef.current = engine;

      gateWallRef.current = null;
      gateActiveRef.current = false;

      const wallOpts = { isStatic: true, restitution: 0.5, friction: 0.05 };
      const walls = [
        // 천장 - 두껍게(80px) + y=0 기준으로 완전히 막음, 반발력 매우 낮게
        Bodies.rectangle(TOTAL_W / 2, -40, TOTAL_W + 40, 80, {
          isStatic: true,
          restitution: 0.1,
          friction: 0.8,
          label: "ceiling",
        }),
        Bodies.rectangle(TOTAL_W / 2, GAME_H + 10, TOTAL_W + 20, 20, wallOpts),
        Bodies.rectangle(-10, GAME_H / 2, 20, GAME_H + 20, wallOpts),
        Bodies.rectangle(TOTAL_W + 10, GAME_H / 2, 20, GAME_H + 20, wallOpts),
        Bodies.rectangle(GAME_W, 750, 6, 100, wallOpts),
        Bodies.rectangle(TOTAL_W - 20, 25, 80, 10, { ...wallOpts, angle: Math.PI * 0.15, restitution: 0.8 }),
        Bodies.rectangle(GAME_W + 10, 80, 90, 10, { ...wallOpts, angle: Math.PI * 0.4, restitution: 0.8 }),
      ];

      // 범퍼 12개 (빨간 8 + 파란 4) - 반발력 매 게임마다 크게 랜덤 변동
      const bumperOpts = { isStatic: true, restitution: 1.5 + Math.random() * 0.5, label: "bumper" };
      const bumperPositions = generateRandomBumperPositions();
      const bumpers = bumperPositions.map((b, i) =>
        Bodies.circle(b.x, b.y, b.r, {
          ...bumperOpts,
          // 반발력을 1.2~2.2 사이로 크게 랜덤화 → 같은 범퍼에 맞아도 매번 다르게 튕김
          restitution: 1.2 + Math.random() * 1.0,
          friction: Math.random() * 0.05,
          label: `bumper-${i}`,
        })
      );
      bumpersRef.current = bumpers;

      // 디플렉터 (3개)
      const deflectorPositions = generateRandomDeflectorPositions();
      const deflectors = deflectorPositions.map((d, i) =>
        Bodies.rectangle(d.x, d.y, d.w, 15, {
          isStatic: true,
          restitution: 0.8 + Math.random() * 0.4,
          angle: d.angle,
          label: `deflector-${i}`,
        })
      );
      deflectorsRef.current = deflectors;

      // 스핀너 3개
      const spinnerPositions = generateRandomSpinnerPositions();
      const spinnerBodies = spinnerPositions.map((s, i) =>
        Bodies.rectangle(s.x, s.y, s.w, 8, {
          isStatic: true,
          restitution: 0.6,
          angle: spinnerAnglesRef.current[i] || 0,
          label: `spinner-${i}`,
        })
      );
      spinnersRef.current = spinnerBodies;

      // 바구니 상단 페그 없음 (제거됨)

      // Basket area - 5줄 × 9칸 = 45번호 (랜덤 배치)
      const basketSensors: any[] = [];

      if (basketNumbersRef.current.length === 0) {
        shuffleBasketNumbers();
      }
      const basketNums = basketNumbersRef.current;

      const basketStartX = 15;
      const basketStartY = 600;
      const basketW = (GAME_W - 30) / BASKET_COLS;
      const basketH = 36;

      for (let row = 0; row < BASKET_ROWS; row++) {
        for (let col = 0; col < BASKET_COLS; col++) {
          const idx = row * BASKET_COLS + col;
          if (idx >= 45) break;
          const num = basketNums[idx];
          const x = basketStartX + col * basketW + basketW / 2;
          const y = basketStartY + row * basketH + basketH / 2;
          basketSensors.push(
            Bodies.rectangle(x, y, basketW - 2, basketH, {
              isStatic: true,
              isSensor: true,
              label: `basket-${num}`,
            })
          );
        }
      }
      basketSensorsRef.current = basketSensors;

      const dividers: any[] = [];
      for (let col = 0; col <= BASKET_COLS; col++) {
        // 1열 왼쪽(col=0)과 9열 오른쪽(col=BASKET_COLS) 구분선 제거 → 끼임 버그 방지
        if (col === 0 || col === BASKET_COLS) continue;
        const x = basketStartX + col * basketW;
        dividers.push(
          Bodies.rectangle(x, basketStartY + (BASKET_ROWS * basketH) / 2, 3, BASKET_ROWS * basketH + 4, {
            isStatic: true,
            restitution: 0.8,
            friction: 0.0,
          })
        );
      }
      for (let row = 0; row <= BASKET_ROWS; row++) {
        const y = basketStartY + row * basketH;
        dividers.push(
          Bodies.rectangle(basketStartX + (BASKET_COLS * basketW) / 2, y, BASKET_COLS * basketW, 3, {
            isStatic: true,
            restitution: 0.8,
            friction: 0.0,
          })
        );
      }

      // 바구니 양쪽 가이드 벽 추가 (1열 왼쪽, 9열 오른쪽) - 공이 외벽과 바구니 사이에 끼이지 않도록
      const basketTotalH = BASKET_ROWS * basketH;
      const leftGuideX = basketStartX - 1;
      const rightGuideX = basketStartX + BASKET_COLS * basketW + 1;
      dividers.push(
        Bodies.rectangle(leftGuideX, basketStartY + basketTotalH / 2, 4, basketTotalH + 10, {
          isStatic: true,
          restitution: 0.3,
          friction: 0.0,
          label: "basket-left-wall",
        })
      );
      dividers.push(
        Bodies.rectangle(rightGuideX, basketStartY + basketTotalH / 2, 4, basketTotalH + 10, {
          isStatic: true,
          restitution: 0.3,
          friction: 0.0,
          label: "basket-right-wall",
        })
      );

      const allBodies = [...walls, ...bumpers, ...deflectors, ...spinnerBodies, ...basketSensors, ...dividers];
      worldBodiesRef.current = allBodies;
      World.add(engine.world, allBodies);

      // Collision events
      Events.on(engine, "collisionStart", (event: any) => {
        event.pairs.forEach((pair: any) => {
          const { bodyA, bodyB } = pair;
          const ball = activeBallRef.current;
          if (!ball) return;
          const isBall = bodyA === ball || bodyB === ball;
          if (!isBall) return;
          const other = bodyA === ball ? bodyB : bodyA;

          const Matter2 = (window as any).Matter;
          if (Matter2 && ball && !ball.isStatic) {
            Matter2.Body.applyForce(ball, ball.position, {
              x: randOffset(0.003),
              y: randOffset(0.002),
            });
          }

          if (other.label?.startsWith("bumper")) {
            bumperFlashRef.current.set(other.label, 1);
            const bIdx = currentBallIndexRef.current;
            const col = BALL_COLORS[bIdx] || BALL_COLORS[0];
            spawnParticles(other.position.x, other.position.y, 8, [col.fill, "#fff", "#fbbf24"]);
            playBumperSound();
          } else if (other.label?.startsWith("deflector")) {
            playDeflectorSound();
          } else if (other.label?.startsWith("spinner")) {
            // 스핀너: 충돌 시 회전 속도 증가
            const spinIdx = parseInt(other.label.split("-")[1]);
            if (!isNaN(spinIdx)) {
              // 회전 방향은 공의 진행 방향에 따라
              const spinDir = ball.velocity.x > 0 ? 1 : -1;
              spinnerAnglesRef.current[spinIdx] += spinDir * 0.5;
            }
            bumperFlashRef.current.set(other.label, 1);
            spawnParticles(other.position.x, other.position.y, 6, ["#a78bfa", "#c084fc", "#fff"], 3);
            playSpinnerSound();
          } else if (!other.label?.startsWith("basket") && !other.isSensor) {
            playWallSound();
          }
        });
      });

      // After‑update – check for settling in a basket
      Events.on(engine, "afterUpdate", () => {
        const ball = activeBallRef.current;
        if (!ball || !isBallFlyingRef.current) return;
        if (settledRef.current.has(ball)) return;

        const { Body } = Matter;

        // 바구니 영역 상수 (이벤트 핸들러 내부에서 사용)
        const basketStartX = 15;
        const basketStartY = 600;
        const basketW = (GAME_W - 30) / BASKET_COLS;
        const basketH = 36;

        // 스핀너 회전 업데이트
        spinnersRef.current.forEach((spinner, idx) => {
          if (spinnerAnglesRef.current[idx] !== undefined) {
            const dir = idx === 1 ? -1 : 1;
            spinnerAnglesRef.current[idx] += 0.02 * dir;
            Body.setAngle(spinner, spinnerAnglesRef.current[idx]);
          }
        });

        // 공이 게임 화면 밖으로 이탈했는지 체크
        const outLeft = ball.position.x < -BALL_R * 3;
        const outRight = ball.position.x > TOTAL_W + BALL_R * 3;
        const outTop = ball.position.y < -BALL_R * 3;
        const outBottom = ball.position.y > GAME_H + BALL_R * 3;
        const isOutOfBounds = (outLeft || outRight || outTop || outBottom) && !blastOffHandledRef.current;

        if (isOutOfBounds) {
          blastOffHandledRef.current = true;

          const blastX = GAME_W / 2;
          const blastY = GAME_H / 2;

          spawnParticles(blastX, blastY, 30, ["#ef4444", "#fb923c", "#fbbf24", "#fff", "#f87171"], 6);
          spawnParticles(blastX, blastY, 15, ["#ff0000", "#ff6600", "#ffcc00"], 4);

          // 별 파티클 생성 - 화성갈끄야 주변 공전
          starParticlesRef.current = [];
          for (let si = 0; si < 12; si++) {
            const baseAngle = (Math.PI * 2 * si) / 12;
            const radius = 55 + Math.random() * 35;
            starParticlesRef.current.push({
              angle: baseAngle,
              radius,
              orbitSpeed: (0.04 + Math.random() * 0.04) * (Math.random() < 0.5 ? 1 : -1),
              life: 1,
              size: 10 + Math.random() * 8,
              rotation: Math.random() * Math.PI * 2,
              rotSpeed: (Math.random() - 0.5) * 0.2,
              color: ["#fbbf24", "#f59e0b", "#fff", "#fcd34d", "#ef4444", "#fb923c"][Math.floor(Math.random() * 6)],
              centerX: blastX,
              centerY: blastY - 70,
            });
          }

          blastOffRef.current.active = true;
          blastOffRef.current.opacity = 1;
          blastOffRef.current.scale = 0.5;
          blastOffRef.current.phase = 0;
          blastOffRef.current.rocketY = GAME_H / 2;

          World.remove(engine.world, ball);
          trailsRef.current.delete(ball);
          activeBallRef.current = null;
          isBallFlyingRef.current = false;

          if (retryTimeoutRef.current) {
            clearTimeout(retryTimeoutRef.current);
            retryTimeoutRef.current = null;
          }

          if (gateWallRef.current) {
            World.remove(engineRef.current.world, gateWallRef.current);
            gateWallRef.current = null;
            gateActiveRef.current = false;
          }

          return;
        }

        if (blastOffHandledRef.current) return;

        if (!gateActiveRef.current && ball.position.x < GAME_W - BALL_R - 5) {
          const gateWall = Bodies.rectangle(GAME_W, GAME_H / 2, 6, GAME_H, {
            isStatic: true,
            restitution: 0.5,
            friction: 0.05,
            label: "gate-wall",
          });
          World.add(engineRef.current.world, gateWall);
          gateWallRef.current = gateWall;
          gateActiveRef.current = true;
        }

        const trail = trailsRef.current.get(ball);
        if (trail) {
          trail.push({ x: ball.position.x, y: ball.position.y, alpha: 1 });
          if (trail.length > 12) trail.shift();
        }

        // ===== 범퍼 사이 끼임 감지 및 강제 탈출 =====
        if (ball.position.y < 580 && !ball.isStatic) {
          const speed = Math.sqrt(ball.velocity.x ** 2 + ball.velocity.y ** 2);
          const lastPos = lastBallPosRef.current;
          const moved = lastPos
            ? Math.sqrt((ball.position.x - lastPos.x) ** 2 + (ball.position.y - lastPos.y) ** 2)
            : 999;

          if (speed < 0.3 && moved < 0.2) {
            stuckTimerRef.current += 16;
          } else {
            stuckTimerRef.current = 0;
          }

          lastBallPosRef.current = { x: ball.position.x, y: ball.position.y };

          // 2.5초 이상 거의 안 움직이면 강제로 아래로 밀기
          if (stuckTimerRef.current > 1500) {
            stuckTimerRef.current = 0;
            const { Body } = Matter;
            Body.setVelocity(ball, {
              x: (Math.random() - 0.5) * 3,
              y: 4 + Math.random() * 3,
            });
            Body.applyForce(ball, ball.position, {
              x: (Math.random() - 0.5) * 0.005,
              y: 0.012 + Math.random() * 0.008,
            });
          }
        } else if (ball.position.y >= 580 && !ball.isStatic && !settledRef.current.has(ball)) {
          // ===== 바구니 영역 끼임 감지 (1열/9열 벽 근처) =====
          const speed = Math.sqrt(ball.velocity.x ** 2 + ball.velocity.y ** 2);
          const lastPos = lastBallPosRef.current;
          const moved = lastPos
            ? Math.sqrt((ball.position.x - lastPos.x) ** 2 + (ball.position.y - lastPos.y) ** 2)
            : 999;

          // 바구니 양쪽 끝 근처(x < 35 또는 x > 525)에서 느리게 움직이면 끼임으로 판단
          const nearLeftWall = ball.position.x < basketStartX + basketW * 0.8;
          const nearRightWall = ball.position.x > basketStartX + BASKET_COLS * basketW - basketW * 0.8;

          if ((nearLeftWall || nearRightWall) && speed < 1.5 && moved < 1.0) {
            stuckTimerRef.current += 16;
          } else {
            stuckTimerRef.current = 0;
          }

          lastBallPosRef.current = { x: ball.position.x, y: ball.position.y };

          // 0.8초 이상 끼여 있으면 안쪽으로 밀어넣기
          if (stuckTimerRef.current > 800) {
            stuckTimerRef.current = 0;
            const { Body } = Matter;
            // 왼쪽 끼임이면 오른쪽으로, 오른쪽 끼임이면 왼쪽으로 밀기
            const pushX = nearLeftWall ? 1.5 + Math.random() * 1.5 : -(1.5 + Math.random() * 1.5);
            Body.setVelocity(ball, {
              x: pushX,
              y: 1.5 + Math.random() * 1.5,
            });
          }
        } else {
          stuckTimerRef.current = 0;
          lastBallPosRef.current = null;
        }

        // ===== 바구니 영역 좌우 벽 끼임 강제 탈출 =====
        if (ball.position.y >= 580 && !hasEnteredBasketRef.current) {
          hasEnteredBasketRef.current = true;
          basketEntryTimeRef.current = Date.now();

          // 진입 시 목표 행 결정:
          // 1행(0): 40% - 1행 기준으로 진입, 자연스럽게 아래로 흘러내릴 수 있음
          // 2행(1): 25%
          // 3행(2): 20%
          // 4행(3): 10%
          // 5행(4): 5%
          const rand = Math.random();
          if (rand < 0.40) {
            targetRowRef.current = 0; // 1행 (40%)
          } else if (rand < 0.65) {
            targetRowRef.current = 1; // 2행 (25%)
          } else if (rand < 0.85) {
            targetRowRef.current = 2; // 3행 (20%)
          } else if (rand < 0.95) {
            targetRowRef.current = 3; // 4행 (10%)
          } else {
            targetRowRef.current = 4; // 5행 (5%)
          }
        }

        // ===== 목표 행까지 부드럽게 유도 (약한 힘만 사용, 자연 낙하 허용) =====
        if (
          hasEnteredBasketRef.current &&
          targetRowRef.current >= 0 &&
          ball.position.y >= 580 &&
          !ball.isStatic &&
          !settledRef.current.has(ball)
        ) {
          const basketStartY = 600;
          const basketH = 36;
          const targetY = basketStartY + targetRowRef.current * basketH + basketH / 2;

          const distBelow = ball.position.y - targetY;
          const distAbove = targetY - ball.position.y;

          if (distAbove > basketH * 0.5) {
            // 목표 행보다 위에 있으면 아래로 살짝 밀기 (약한 힘)
            const pushStrength = Math.min(0.008, distAbove * 0.0002);
            Matter.Body.applyForce(ball, ball.position, { x: 0, y: pushStrength });
          } else if (distBelow > basketH * 0.6) {
            // 목표 행보다 아래로 많이 내려갔을 때만 약하게 저항
            // (자연스럽게 1~2행 아래까지는 흘러내려도 OK)
            const brakeStrength = Math.min(0.015, distBelow * 0.0004);
            Matter.Body.applyForce(ball, ball.position, { x: 0, y: -brakeStrength });
            if (ball.velocity.y > 2.5) {
              Matter.Body.setVelocity(ball, {
                x: ball.velocity.x * 0.9,
                y: ball.velocity.y * 0.75,
              });
            }
          } else {
            // 목표 달성 후 제어 해제 → 이후 중력에 의해 자연스럽게 안착
            targetRowRef.current = -1;
          }
        }

        // ===== 바구니 하단 이탈 방지 안전장치 =====
        {
          const basketStartY = 600;
          const basketH = 36;
          const basketBottomY = basketStartY + BASKET_ROWS * basketH;
          if (
            ball.position.y > basketBottomY - BALL_R &&
            !ball.isStatic &&
            !settledRef.current.has(ball)
          ) {
            // 바구니 마지막 행 안으로 강제 복귀
            Matter.Body.setVelocity(ball, { x: ball.velocity.x * 0.3, y: -1.0 });
            Matter.Body.setPosition(ball, {
              x: ball.position.x,
              y: basketBottomY - basketH * 0.5,
            });
          }
        }

        // 자연스러운 물리 시뮬레이션에 맡김

        // ===== 바구니 내부 강제 하강 로직 제거 (버전 292 방식) =====
        // 안착 판정은 물리 시뮬레이션에 맡김

        if (hasEnteredBasketRef.current && basketEntryTimeRef.current > 0) {
          const elapsedSinceEntry = Date.now() - basketEntryTimeRef.current;
          if (elapsedSinceEntry > BASKET_ENTRY_TIMEOUT) {
            const Matter2 = (window as any).Matter;
            if (Matter2 && engineRef.current) {
              const { World: World2 } = Matter2;
              const oldBall = activeBallRef.current;

              if (oldBall) {
                World2.remove(engineRef.current.world, oldBall);
                trailsRef.current.delete(oldBall);
                activeBallRef.current = null;
                isBallFlyingRef.current = false;

                if (gateWallRef.current) {
                  World2.remove(engineRef.current.world, gateWallRef.current);
                  gateWallRef.current = null;
                  gateActiveRef.current = false;
                }

                if (retryTimeoutRef.current) {
                  clearTimeout(retryTimeoutRef.current);
                  retryTimeoutRef.current = null;
                }

                setTimeout(() => {
                  prepareBallFnRef.current();
                }, 100);
              }
            }
            return;
          }
        }

        const speed = Math.sqrt(ball.velocity.x ** 2 + ball.velocity.y ** 2);
        if (speed < 0.5 && ball.position.y > 580) {
          const timer = stableTimersRef.current.get(ball) || 0;
          stableTimersRef.current.set(ball, timer + 16);

          // ===== 1행 안착 직전 30% 확률로 아래 행으로 강제 이동 =====
          if (timer === 0 || (timer > 0 && timer < 32)) {
            const basketStartY2 = 600;
            const basketH2 = 36;
            const currentRow = Math.floor((ball.position.y - basketStartY2) / basketH2);
            void currentRow;
            // 1행 안착 직전 추가 밀어내기 로직 제거 - 진입 시 목표 행으로만 제어
          }

          if (timer + 16 > 250) {
            let closestNum = -1;
            let closestDist = Infinity;
            basketSensors.forEach((sensor) => {
              const dx = ball.position.x - sensor.position.x;
              const dy = ball.position.y - sensor.position.y;
              const dist = Math.sqrt(dx * dx + dy * dy);
              if (dist < closestDist) {
                closestDist = dist;
                closestNum = parseInt(sensor.label.split("-")[1]);
              }
            });

            if (closestNum > 0 && closestDist < 45) {
              const alreadySettled = Array.from(settledRef.current.values());
              
              let finalNum = closestNum;
              let finalSensor = basketSensorsRef.current.find((s) => s.label === `basket-${closestNum}`);
              
              if (alreadySettled.includes(closestNum)) {
                const basketNums2 = basketNumbersRef.current;
                const currentIdx = basketNums2.indexOf(closestNum);
                const currentCol = currentIdx % BASKET_COLS;
                
                let foundEmpty = false;
                const currentRow = Math.floor(currentIdx / BASKET_COLS);

                // 1단계: 같은 열의 아래 행 탐색
                for (let row = currentRow + 1; row < BASKET_ROWS; row++) {
                  const checkIdx = row * BASKET_COLS + currentCol;
                  if (checkIdx < 45) {
                    const checkNum = basketNums2[checkIdx];
                    if (!alreadySettled.includes(checkNum)) {
                      finalNum = checkNum;
                      finalSensor = basketSensorsRef.current.find((s) => s.label === `basket-${checkNum}`);
                      foundEmpty = true;
                      break;
                    }
                  }
                }
                
                // 2단계: 바로 옆 열(±1)만 탐색 - 물리적으로 자연스러운 범위
                if (!foundEmpty) {
                  for (const dir of [-1, 1]) {
                    const checkCol = currentCol + dir;
                    if (checkCol < 0 || checkCol >= BASKET_COLS) continue;
                    // 같은 행부터 아래 행 순서로 탐색
                    for (let row = currentRow; row < BASKET_ROWS; row++) {
                      const checkIdx = row * BASKET_COLS + checkCol;
                      if (checkIdx < 45) {
                        const checkNum = basketNums2[checkIdx];
                        if (!alreadySettled.includes(checkNum)) {
                          finalNum = checkNum;
                          finalSensor = basketSensorsRef.current.find((s) => s.label === `basket-${checkNum}`);
                          foundEmpty = true;
                          break;
                        }
                      }
                    }
                    if (foundEmpty) break;
                  }
                }

                // 3단계: ±1열에도 없으면 공을 튕겨내서 다시 시도 (멀리 이동 금지)
                if (!foundEmpty) {
                  Body.setVelocity(ball, {
                    x: (Math.random() - 0.5) * 8,
                    y: -4 - Math.random() * 3,
                  });
                  stableTimersRef.current.set(ball, 0);
                  spawnParticles(ball.position.x, ball.position.y, 6, ["#ef4444", "#fca5a5"]);
                  playRejectSound();
                  return;
                }
                
                spawnParticles(ball.position.x, ball.position.y, 4, ["#60a5fa", "#93c5fd"], 2);
              }

              if (retryTimeoutRef.current) {
                clearTimeout(retryTimeoutRef.current);
                retryTimeoutRef.current = null;
              }

              settledRef.current.set(ball, finalNum);
              Body.setStatic(ball, true);
              // 공을 바구니 셀 정확히 위치
              if (finalSensor) {
                Body.setPosition(ball, { x: finalSensor.position.x, y: finalSensor.position.y });
              }
              const bIdx = currentBallIndexRef.current;
              const col = BALL_COLORS[bIdx] || BALL_COLORS[0];
              spawnParticles(ball.position.x, ball.position.y, 12, [col.fill, col.highlight, "#fff"], 4);
              playBasketSound();

              goodEffectRef.current.active = true;
              goodEffectRef.current.opacity = 1;
              goodEffectRef.current.x = ball.position.x;
              goodEffectRef.current.y = ball.position.y - 40;
              goodEffectRef.current.phase = 0;

              settledBallsRef.current.push({ ball, index: bIdx });
              isBallFlyingRef.current = false;
              onBallSettled(bIdx, finalNum);

              if (settledRef.current.size === BALL_COUNT) {
                const results: number[] = [];
                settledBallsRef.current
                  .sort((a, b) => a.index - b.index)
                  .forEach((item) => {
                    results.push(settledRef.current.get(item.ball) || 0);
                  });
                setTimeout(() => {
                  playCompleteSound();
                  for (let k = 0; k < 5; k++) {
                    setTimeout(() => {
                      spawnParticles(
                        100 + Math.random() * (GAME_W - 200),
                        300 + Math.random() * 200,
                        20,
                        ["#fbbf24", "#f87171", "#34d399", "#60a5fa", "#c084fc", "#fb923c", "#fff"],
                        5
                      );
                    }, k * 200);
                  }
                  onAllNumbersSelected(results);
                  onLaunchEnd();
                }, 600);
              }
            } else {
              stableTimersRef.current.set(ball, 0);
            }
          }
        } else {
          stableTimersRef.current.set(ball, 0);
        }
      });
      // Game loop
      runnerRef.current.enabled = true;
      const loop = () => {
        if (!runnerRef.current.enabled) return;
        Engine.update(engine, 1000 / 60);

        const canvas = canvasRef.current;
        if (!canvas) { rafRef.current = requestAnimationFrame(loop); return; }
        const ctx = canvas.getContext("2d");
        if (!ctx) { rafRef.current = requestAnimationFrame(loop); return; }

        if (plungerRef.current.springAnim > 0) {
          plungerRef.current.springAnim -= 0.08;
          if (plungerRef.current.springAnim < 0) plungerRef.current.springAnim = 0;
          plungerRef.current.currentY = plungerRef.current.baseY - plungerRef.current.springAnim * 20;
          plungerRef.current.pullAmount = 0;
        }

        const Matter2 = (window as any).Matter;
        if (Matter2) {
          spinnersRef.current.forEach((spinner, idx) => {
            if (spinnerAnglesRef.current[idx] !== undefined) {
              const dir = idx === 1 ? -1 : 1;
              spinnerAnglesRef.current[idx] += 0.015 * dir;
              Matter2.Body.setAngle(spinner, spinnerAnglesRef.current[idx]);
            }
          });
        }

        if (blastOffRef.current.active) {
          blastOffRef.current.phase += 0.016;
          blastOffRef.current.rocketY -= 4;
          if (blastOffRef.current.phase < 0.4) {
            blastOffRef.current.opacity = Math.min(1, blastOffRef.current.phase * 4);
            blastOffRef.current.scale = 0.5 + blastOffRef.current.phase * 2.5;
          } else {
            blastOffRef.current.opacity -= 0.025;
            blastOffRef.current.scale += 0.005;
          }
          if (blastOffRef.current.opacity <= 0) {
            blastOffRef.current.active = false;
            starParticlesRef.current = [];
            if (blastOffHandledRef.current && !activeBallRef.current) {
              setTimeout(() => {
                prepareBallFnRef.current();
              }, 100);
            }
          }
        }

        // ===== 배경 그리기 =====
        ctx.clearRect(0, 0, TOTAL_W, GAME_H);

        ctx.save();
        ctx.beginPath();
        ctx.rect(0, 0, GAME_W, GAME_H);
        ctx.clip();
        const bgGrad = ctx.createRadialGradient(GAME_W / 2, GAME_H * 0.25, 50, GAME_W / 2, GAME_H * 0.25, GAME_W);
        bgGrad.addColorStop(0, "#fbbf24");
        bgGrad.addColorStop(0.35, "#f59e0b");
        bgGrad.addColorStop(0.65, "#d97706");
        bgGrad.addColorStop(1, "#78350f");
        ctx.fillStyle = bgGrad;
        ctx.fill();
        ctx.restore();

        ctx.strokeStyle = "#92400e";
        ctx.lineWidth = 5;
        ctx.beginPath();
        ctx.arc(GAME_W / 2, 0, 240, 0.12 * Math.PI, 0.88 * Math.PI);
        ctx.stroke();
        ctx.strokeStyle = "#b45309";
        ctx.lineWidth = 2;
        ctx.beginPath();
        ctx.arc(GAME_W / 2, 0, 236, 0.12 * Math.PI, 0.88 * Math.PI);
        ctx.stroke();

        // ===== 범퍼 =====
        bumpersRef.current.forEach((bumper: any, idx: number) => {
          const r = bumper.circleRadius || 32;
          const flash = bumperFlashRef.current.get(bumper.label) || 0;
          const bumperData = randomBumperPositionsRef.current[idx];
          const isBlue = bumperData?.type === "blue";

          if (flash > 0) {
            ctx.beginPath();
            ctx.arc(bumper.position.x, bumper.position.y, r + 12, 0, Math.PI * 2);
            ctx.fillStyle = isBlue
              ? `rgba(59, 130, 246, ${flash * 0.5})`
              : `rgba(239, 68, 68, ${flash * 0.5})`;
            ctx.fill();
            bumperFlashRef.current.set(bumper.label, Math.max(0, flash - 0.025));
          }

          ctx.beginPath();
          ctx.arc(bumper.position.x, bumper.position.y, r, 0, Math.PI * 2);
          const bGrad = ctx.createRadialGradient(
            bumper.position.x - r * 0.3,
            bumper.position.y - r * 0.3,
            r * 0.1,
            bumper.position.x,
            bumper.position.y,
            r
          );
          if (isBlue) {
            bGrad.addColorStop(0, "#60a5fa");
            bGrad.addColorStop(0.6, "#3b82f6");
            bGrad.addColorStop(1, "#1d4ed8");
          } else {
            bGrad.addColorStop(0, "#ff6b6b");
            bGrad.addColorStop(0.6, "#ef4444");
            bGrad.addColorStop(1, "#b91c1c");
          }
          ctx.fillStyle = bGrad;
          ctx.fill();
          ctx.strokeStyle = isBlue ? "#93c5fd" : "#fbbf24";
          ctx.lineWidth = 3;
          ctx.stroke();
          ctx.beginPath();
          ctx.arc(bumper.position.x, bumper.position.y, r * 0.5, 0, Math.PI * 2);
          ctx.strokeStyle = "rgba(255,255,255,0.35)";
          ctx.lineWidth = 2;
          ctx.stroke();
          ctx.font = "bold 14px Orbitron, sans-serif";
          ctx.fillStyle = "#fff";
          ctx.textAlign = "center";
          ctx.textBaseline = "middle";
          ctx.fillText("\u2605", bumper.position.x, bumper.position.y);
        });

        // ===== 디플렉터 =====
        deflectorsRef.current.forEach((d: any, idx: number) => {
          ctx.save();
          ctx.translate(d.position.x, d.position.y);
          ctx.rotate(d.angle);
          const dData = randomDeflectorPositionsRef.current[idx];
          const w = dData ? dData.w : 55;
          const h = 15;
          const dGrad = ctx.createLinearGradient(-w / 2, 0, w / 2, 0);
          dGrad.addColorStop(0, "#1e40af");
          dGrad.addColorStop(0.5, "#3b82f6");
          dGrad.addColorStop(1, "#1e40af");
          ctx.fillStyle = dGrad;
          ctx.beginPath();
          ctx.roundRect(-w / 2, -h / 2, w, h, 5);
          ctx.fill();
          ctx.strokeStyle = "#60a5fa";
          ctx.lineWidth = 1.5;
          ctx.stroke();
          ctx.restore();
        });

        // ===== 스피너 =====
        spinnersRef.current.forEach((s: any, idx: number) => {
          const flash = bumperFlashRef.current.get(s.label) || 0;
          const sData = randomSpinnerPositionsRef.current[idx];
          if (!sData) return;
          const angle = spinnerAnglesRef.current[idx] || 0;

          ctx.save();
          ctx.translate(s.position.x, s.position.y);

          // 플래시 글로우
          if (flash > 0) {
            ctx.beginPath();
            ctx.arc(0, 0, sData.w / 2 + 10, 0, Math.PI * 2);
            ctx.fillStyle = `rgba(167, 139, 250, ${flash * 0.4})`;
            ctx.fill();
            bumperFlashRef.current.set(s.label, Math.max(0, flash - 0.025));
          }

          // 중앙 축
          ctx.beginPath();
          ctx.arc(0, 0, 6, 0, Math.PI * 2);
          const axleGrad = ctx.createRadialGradient(0, 0, 1, 0, 0, 6);
          axleGrad.addColorStop(0, "#e9d5ff");
          axleGrad.addColorStop(1, "#7c3aed");
          ctx.fillStyle = axleGrad;
          ctx.fill();
          ctx.strokeStyle = "#a78bfa";
          ctx.lineWidth = 1.5;
          ctx.stroke();

          // 회전 막대
          ctx.rotate(angle);
          const w = sData.w;
          const h2 = 8;
          const sGrad = ctx.createLinearGradient(-w / 2, 0, w / 2, 0);
          sGrad.addColorStop(0, "#7c3aed");
          sGrad.addColorStop(0.3, "#a78bfa");
          sGrad.addColorStop(0.5, "#c084fc");
          sGrad.addColorStop(0.7, "#a78bfa");
          sGrad.addColorStop(1, "#7c3aed");
          ctx.fillStyle = sGrad;
          ctx.beginPath();
          ctx.roundRect(-w / 2, -h2 / 2, w, h2, 4);
          ctx.fill();
          ctx.strokeStyle = "#c084fc";
          ctx.lineWidth = 1.5;
          ctx.stroke();

          // 양쪽 끝 원형 장식
          [-1, 1].forEach((dir) => {
            ctx.beginPath();
            ctx.arc((w / 2) * dir, 0, 5, 0, Math.PI * 2);
            ctx.fillStyle = "#c084fc";
            ctx.fill();
            ctx.strokeStyle = "#e9d5ff";
            ctx.lineWidth = 1;
            ctx.stroke();
          });
          ctx.restore();
        });

        // ===== 바구니 영역 =====
        const bsX = 15;
        const bsY = 600;
        const bW = (GAME_W - 30) / BASKET_COLS;
        const bH = 36;

        ctx.fillStyle = "rgba(0,0,0,0.3)";
        ctx.beginPath();
        ctx.roundRect(bsX - 5, bsY - 5, BASKET_COLS * bW + 10, BASKET_ROWS * bH + 10, 10);
        ctx.fill();

        const settledNums = Array.from(settledRef.current.values());
        const bNums = basketNumbersRef.current.length > 0
          ? basketNumbersRef.current
          : Array.from({ length: 45 }, (_, i) => i + 1);

        for (let row = 0; row < BASKET_ROWS; row++) {
          for (let col = 0; col < BASKET_COLS; col++) {
            const idx = row * BASKET_COLS + col;
            if (idx >= 45) break;
            const num = bNums[idx];
            const x = bsX + col * bW;
            const y = bsY + row * bH;
            const isSettled = settledNums.includes(num);

            ctx.beginPath();
            ctx.roundRect(x + 1, y + 1, bW - 2, bH - 2, 4);

            if (isSettled) {
              let ballIdx = 0;
              settledRef.current.forEach((val, key) => {
                if (val === num) {
                  const found = settledBallsRef.current.find((item) => item.ball === key);
                  if (found) ballIdx = found.index;
                }
              });
              const col2 = BALL_COLORS[ballIdx] || BALL_COLORS[0];
              const selGrad = ctx.createLinearGradient(x, y, x, y + bH);
              selGrad.addColorStop(0, col2.fill);
              selGrad.addColorStop(1, col2.stroke);
              ctx.fillStyle = selGrad;
              ctx.fill();
              ctx.shadowColor = col2.fill;
              ctx.shadowBlur = 10;
              ctx.fill();
              ctx.shadowBlur = 0;

              // 안착된 칸: 번호를 바구니 셀 중앙에 크고 선명하게 표시
              ctx.font = `bold ${num >= 10 ? 13 : 15}px Orbitron, sans-serif`;
              ctx.fillStyle = "#fff";
              ctx.textAlign = "center";
              ctx.textBaseline = "middle";
              ctx.shadowColor = "rgba(0,0,0,0.8)";
              ctx.shadowBlur = 6;
              ctx.fillText(String(num), x + bW / 2, y + bH / 2);
              ctx.shadowBlur = 0;
            } else {
              const cellGrad = ctx.createLinearGradient(x, y, x, y + bH);
              cellGrad.addColorStop(0, "rgba(255,255,255,0.12)");
              cellGrad.addColorStop(1, "rgba(255,255,255,0.04)");
              ctx.fillStyle = cellGrad;
              ctx.fill();
            }

            ctx.strokeStyle = isSettled ? "rgba(255,255,255,0.6)" : "rgba(255,255,255,0.15)";
            ctx.lineWidth = isSettled ? 2 : 1;
            ctx.stroke();

            // 모든 칸에 번호 표시 (안착된 칸도 포함)
            ctx.font = `bold ${num >= 10 ? 13 : 15}px Orbitron, sans-serif`;
            ctx.fillStyle = isSettled ? "#fff" : "rgba(255,255,255,0.75)";
            ctx.textAlign = "center";
            ctx.textBaseline = "middle";
            if (isSettled) {
              ctx.shadowColor = "rgba(0,0,0,0.8)";
              ctx.shadowBlur = 6;
            }
            ctx.fillText(String(num), x + bW / 2, y + bH / 2);
            ctx.shadowBlur = 0;
          }
        }

        // ===== 트레일 =====
        const allBallEntries = [...settledBallsRef.current.map((item) => ({ ball: item.ball, idx: item.index }))];
        if (activeBallRef.current && isBallFlyingRef.current) {
          allBallEntries.push({ ball: activeBallRef.current, idx: currentBallIndexRef.current });
        }

        allBallEntries.forEach(({ ball, idx }) => {
          const trail = trailsRef.current.get(ball);
          if (!trail || trail.length < 2) return;
          const col = BALL_COLORS[idx] || BALL_COLORS[0];

          for (let t = 0; t < trail.length - 1; t++) {
            const p = trail[t];
            const alpha = (t / trail.length) * 0.4;
            ctx.beginPath();
            ctx.arc(p.x, p.y, BALL_R * (t / trail.length) * 0.7, 0, Math.PI * 2);
            ctx.fillStyle = col.fill;
            ctx.globalAlpha = alpha;
            ctx.fill();
          }
          ctx.globalAlpha = 1;
        });

        // ===== GOOD 효과 =====
        if (goodEffectRef.current.active) {
          goodEffectRef.current.phase += 0.016;
          if (goodEffectRef.current.phase < 0.4) {
            goodEffectRef.current.opacity = Math.min(1, goodEffectRef.current.phase * 4);
          } else {
            goodEffectRef.current.opacity -= 0.02;
          }
          if (goodEffectRef.current.opacity <= 0) {
            goodEffectRef.current.active = false;
          }
          ctx.save();
          ctx.globalAlpha = Math.max(0, goodEffectRef.current.opacity);
          ctx.font = "bold 16px Orbitron, sans-serif";
          ctx.fillStyle = "#fbbf24";
          ctx.textAlign = "center";
          ctx.textBaseline = "middle";
          ctx.shadowColor = "#ef4444";
          ctx.shadowBlur = 10;
          ctx.fillText("GOOD! \uD83D\uDC4D", goodEffectRef.current.x, goodEffectRef.current.y);
          ctx.shadowBlur = 0;
          ctx.restore();
        }

        // ===== 파티클 =====
        particlesRef.current = particlesRef.current.filter((p) => p.life > 0);
        particlesRef.current.forEach((p) => {
          p.x += p.vx;
          p.y += p.vy;
          p.vy += 0.12;
          p.vx *= 0.97;
          p.life -= 0.022;
          ctx.beginPath();
          ctx.arc(p.x, p.y, Math.max(0, p.size * p.life), 0, Math.PI * 2);
          ctx.fillStyle = p.color;
          ctx.globalAlpha = Math.max(0, p.life);
          ctx.fill();
        });
        ctx.globalAlpha = 1;

        // ===== BLAST OFF 텍스트 =====
        if (blastOffRef.current.active) {
          const fixedCenterX = GAME_W / 2;
          const fixedCenterY = GAME_H / 2;

          ctx.save();
          ctx.globalAlpha = Math.max(0, blastOffRef.current.opacity);

          // 🚀 로켓 이모지만 위로 날아감
          ctx.font = "60px sans-serif";
          ctx.textAlign = "center";
          ctx.textBaseline = "middle";
          ctx.fillText("🚀", fixedCenterX, blastOffRef.current.rocketY);

          // 불꽃 이모지 (로켓 바로 아래)
          ctx.font = "28px sans-serif";
          ctx.fillText("🔥🔥🔥", fixedCenterX, blastOffRef.current.rocketY + 55);

          ctx.restore();

          // 화성갈끄야 텍스트 - 화면 가운데 고정
          ctx.save();
          ctx.globalAlpha = Math.max(0, blastOffRef.current.opacity);
          ctx.translate(fixedCenterX, fixedCenterY);
          ctx.scale(blastOffRef.current.scale, blastOffRef.current.scale);

          ctx.font = "bold 38px Orbitron, sans-serif";
          ctx.fillStyle = "#fbbf24";
          ctx.textAlign = "center";
          ctx.textBaseline = "middle";
          ctx.shadowColor = "#ef4444";
          ctx.shadowBlur = 22;
          ctx.fillText("화성갈끄야!", 0, 0);
          ctx.shadowBlur = 0;

          ctx.restore();

          // ===== 별 파티클 공전 그리기 =====
          starParticlesRef.current.forEach((star) => {
            star.angle += star.orbitSpeed;
            star.rotation += star.rotSpeed;

            const sx = star.centerX + Math.cos(star.angle) * star.radius;
            const sy = star.centerY + Math.sin(star.angle) * star.radius;

            ctx.save();
            ctx.globalAlpha = Math.max(0, blastOffRef.current.opacity * star.life);
            ctx.translate(sx, sy);
            ctx.rotate(star.rotation);
            ctx.font = `${star.size}px sans-serif`;
            ctx.textAlign = "center";
            ctx.textBaseline = "middle";
            ctx.fillText("⭐", 0, 0);
            ctx.restore();
          });
        }

        // ===== 공 그리기 함수 =====
        const drawBall = (ball: any, bIdx2: number) => {
          const col = BALL_COLORS[bIdx2] || BALL_COLORS[0];
          const bx = ball.position.x;
          const by = ball.position.y;

          // 그림자
          ctx.beginPath();
          ctx.arc(bx + 3, by + 3, BALL_R, 0, Math.PI * 2);
          ctx.fillStyle = "rgba(0,0,0,0.25)";
          ctx.fill();

          // 글로우
          ctx.beginPath();
          ctx.arc(bx, by, BALL_R + 5, 0, Math.PI * 2);
          ctx.fillStyle = col.glow;
          ctx.fill();

          // 공 본체
          ctx.beginPath();
          ctx.arc(bx, by, BALL_R, 0, Math.PI * 2);
          const ballGrad = ctx.createRadialGradient(bx - 3, by - 3, 2, bx + 3, by + 3, BALL_R);
          ballGrad.addColorStop(0, col.fill);
          ballGrad.addColorStop(0.75, col.fill);
          ballGrad.addColorStop(1, col.stroke);
          ctx.fillStyle = ballGrad;
          ctx.fill();
          ctx.strokeStyle = col.stroke;
          ctx.lineWidth = 1.5;
          ctx.stroke();

          // 하이라이트
          ctx.beginPath();
          ctx.arc(bx - 3, by - 3, 2.5, 0, Math.PI * 2);
          ctx.fillStyle = "rgba(255,255,255,0.5)";
          ctx.fill();

          // 날아가는 공: 순서 번호 표시
          const settledNum = settledRef.current.get(ball);
          if (settledNum === undefined) {
            ctx.font = "bold 8px Orbitron, sans-serif";
            ctx.fillStyle = "#fff";
            ctx.textAlign = "center";
            ctx.textBaseline = "middle";
            ctx.fillText(String(bIdx2 + 1), bx, by + 0.5);
          }
        };

        // 안착된 공 그리기 (바구니 번호는 바구니 셀에 이미 표시됨)
        settledBallsRef.current.forEach((item) => drawBall(item.ball, item.index));

        // ===== 플런저 영역 (공 그리기 이후에 클리핑) =====
        const plungerX = GAME_W + PLUNGER_AREA_W / 2;

        ctx.save();
        ctx.beginPath();
        ctx.rect(GAME_W, 0, PLUNGER_AREA_W, GAME_H);
        ctx.clip();

        const laneBg = ctx.createLinearGradient(GAME_W, 0, TOTAL_W, 0);
        laneBg.addColorStop(0, "#4a2c0a");
        laneBg.addColorStop(0.5, "#5c3610");
        laneBg.addColorStop(1, "#4a2c0a");
        ctx.fillStyle = laneBg;
        ctx.fillRect(GAME_W, 0, PLUNGER_AREA_W, GAME_H);

        ctx.strokeStyle = "#92400e";
        ctx.lineWidth = 3;
        ctx.beginPath();
        ctx.moveTo(GAME_W, 500);
        ctx.lineTo(GAME_W, GAME_H);
        ctx.stroke();

        ctx.strokeStyle = "rgba(255,255,255,0.05)";
        ctx.lineWidth = 1;
        for (let gy = 80; gy < GAME_H - 50; gy += 20) {
          ctx.beginPath();
          ctx.moveTo(GAME_W + 8, gy);
          ctx.lineTo(GAME_W + PLUNGER_AREA_W - 8, gy);
          ctx.stroke();
        }

        if (gateActiveRef.current) {
          ctx.strokeStyle = "#ef4444";
          ctx.lineWidth = 4;
          ctx.shadowColor = "#ef4444";
          ctx.shadowBlur = 8;
          ctx.beginPath();
          ctx.moveTo(GAME_W, 0);
          ctx.lineTo(GAME_W, GAME_H);
          ctx.stroke();
          ctx.shadowBlur = 0;
        } else {
          ctx.strokeStyle = "rgba(146, 64, 14, 0.4)";
          ctx.lineWidth = 2;
          ctx.setLineDash([6, 6]);
          ctx.beginPath();
          ctx.moveTo(GAME_W, 0);
          ctx.lineTo(GAME_W, 700);
          ctx.stroke();
          ctx.setLineDash([]);
        }

        // 플런저 스프링 - 핸들 바로 위 짧은 범위만 그리기
        {
          const plunger2 = plungerRef.current;
          const pullOffset2 = plunger2.pullAmount * plunger2.maxPull * 0.3;
          const handleY2 = plunger2.baseY + pullOffset2;
          const springTop2 = handleY2 - 60;
          const springCoilCount = 8;
          const springCoilSpacing = (handleY2 - springTop2) / springCoilCount;

          ctx.strokeStyle = "#9ca3af";
          ctx.lineWidth = 3;
          ctx.beginPath();
          for (let c = 0; c <= springCoilCount; c++) {
            const cy = springTop2 + c * springCoilSpacing;
            const cx = c % 2 === 0 ? plungerX - 10 : plungerX + 10;
            if (c === 0) ctx.moveTo(cx, cy);
            else ctx.lineTo(cx, cy);
          }
          ctx.stroke();
        }

        const plunger = plungerRef.current;
        const pullOffset = plunger.pullAmount * plunger.maxPull * 0.3;
        const handleY = plungerRef.current.baseY + pullOffset;
        const handleW = 36;
        const handleGrad = ctx.createLinearGradient(plungerX - handleW / 2, handleY, plungerX + handleW / 2, handleY);
        handleGrad.addColorStop(0, "#dc2626");
        handleGrad.addColorStop(0.3, "#ef4444");
        handleGrad.addColorStop(0.5, "#f87171");
        handleGrad.addColorStop(0.7, "#ef4444");
        handleGrad.addColorStop(1, "#dc2626");
        ctx.fillStyle = handleGrad;
        ctx.beginPath();
        ctx.roundRect(plungerX - handleW / 2, handleY, handleW, 50, 8);
        ctx.fill();
        ctx.strokeStyle = "#991b1b";
        ctx.lineWidth = 2;
        ctx.stroke();

        // 핸들 줄무늬
        ctx.strokeStyle = "rgba(255,255,255,0.2)";
        ctx.lineWidth = 1.5;
        for (let gl = 0; gl < 3; gl++) {
          const gy = handleY + 15 + gl * 10;
          ctx.beginPath();
          ctx.moveTo(plungerX - 10, gy);
          ctx.lineTo(plungerX + 10, gy);
          ctx.stroke();
        }

        // 핸들 상단 캡
        const capGrad = ctx.createRadialGradient(plungerX, handleY, 2, plungerX, handleY, 18);
        capGrad.addColorStop(0, "#fca5a5");
        capGrad.addColorStop(1, "#b91c1c");
        ctx.fillStyle = capGrad;
        ctx.beginPath();
        ctx.arc(plungerX, handleY, 16, Math.PI, 0);
        ctx.fill();
        ctx.strokeStyle = "#991b1b";
        ctx.lineWidth = 2;
        ctx.stroke();

        if (plungerRef.current.pullAmount > 0.02) {
          const powerH = 100;
          const powerY = plungerRef.current.baseY - 180;
          const powerX = GAME_W + 8;
          const powerW = 8;

          ctx.fillStyle = "rgba(0,0,0,0.4)";
          ctx.beginPath();
          ctx.roundRect(powerX, powerY, powerW, powerH, 4);
          ctx.fill();

          const fillH = powerH * plungerRef.current.pullAmount;
          const powerGrad = ctx.createLinearGradient(powerX, powerY + powerH, powerX, powerY);
          powerGrad.addColorStop(0, "#22c55e");
          powerGrad.addColorStop(0.5, "#eab308");
          powerGrad.addColorStop(1, "#ef4444");
          ctx.fillStyle = powerGrad;
          ctx.beginPath();
          ctx.roundRect(powerX, powerY + powerH - fillH, powerW, fillH, 4);
          ctx.fill();
        }

        for (let i = plungerRef.current.canLaunch ? currentBallIndexRef.current : 0; i < BALL_COUNT; i++) {
          const col = BALL_COLORS[i];
          const indicatorY = GAME_H - 30 - (i - currentBallIndexRef.current) * 28;
          ctx.beginPath();
          ctx.arc(plungerX, indicatorY, 8, 0, Math.PI * 2);
          ctx.fillStyle = i === currentBallIndexRef.current ? col.fill : col.fill + "60";
          ctx.fill();
          ctx.strokeStyle = i === currentBallIndexRef.current ? col.stroke : col.stroke + "40";
          ctx.lineWidth = 1.5;
          ctx.stroke();
          ctx.font = "bold 7px Orbitron, sans-serif";
          ctx.fillStyle = "#fff";
          ctx.textAlign = "center";
          ctx.textBaseline = "middle";
          ctx.fillText(String(i + 1), plungerX, indicatorY);
        }

        if (plungerRef.current.canLaunch && plungerRef.current.ballWaiting && !isCompleteRef.current) {
          ctx.save();
          const t = Date.now() * 0.004;
          const bounceY = Math.sin(t) * 4;
          const pulseAlpha = 0.6 + Math.sin(t * 1.2) * 0.35;
          const guideY = handleY - 90 + bounceY;

          ctx.beginPath();
          ctx.arc(plungerX, guideY + 10, 28, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(251, 191, 36, ${pulseAlpha * 0.2})`;
          ctx.fill();

          ctx.globalAlpha = pulseAlpha;
          ctx.fillStyle = "#fbbf24";
          ctx.beginPath();
          ctx.moveTo(plungerX - 12, guideY);
          ctx.lineTo(plungerX + 12, guideY);
          ctx.lineTo(plungerX, guideY + 16);
          ctx.closePath();
          ctx.fill();

          ctx.globalAlpha = pulseAlpha * 0.5;
          ctx.beginPath();
          ctx.moveTo(plungerX - 10, guideY + 18);
          ctx.lineTo(plungerX + 10, guideY + 18);
          ctx.lineTo(plungerX, guideY + 30);
          ctx.closePath();
          ctx.fill();

          ctx.globalAlpha = pulseAlpha;
          ctx.font = "bold 11px Orbitron, sans-serif";
          ctx.fillStyle = "#fbbf24";
          ctx.textAlign = "center";
          ctx.textBaseline = "middle";
          ctx.shadowColor = "rgba(251, 191, 36, 0.6)";
          ctx.shadowBlur = 6;
          ctx.fillText("PULL \u25BC", plungerX, guideY - 28);
          ctx.shadowBlur = 0;
          ctx.restore();
        }

        // ===== 플런저 레인 위 대기 공 다시 그리기 (클리핑 영역 안에서) =====
        if (activeBallRef.current && !isBallFlyingRef.current) {
          const waitBall = activeBallRef.current;
          const bIdx2 = currentBallIndexRef.current;
          const col = BALL_COLORS[bIdx2] || BALL_COLORS[0];
          const bx = waitBall.position.x;
          const by = waitBall.position.y;

          ctx.beginPath();
          ctx.arc(bx + 2, by + 2, BALL_R, 0, Math.PI * 2);
          ctx.fillStyle = "rgba(0,0,0,0.25)";
          ctx.fill();

          ctx.beginPath();
          ctx.arc(bx, by, BALL_R + 5, 0, Math.PI * 2);
          ctx.fillStyle = col.glow;
          ctx.fill();

          ctx.beginPath();
          ctx.arc(bx, by, BALL_R, 0, Math.PI * 2);
          const ballGrad2 = ctx.createRadialGradient(bx - 3, by - 3, 2, bx + 3, by + 3, BALL_R);
          ballGrad2.addColorStop(0, col.fill);
          ballGrad2.addColorStop(0.75, col.fill);
          ballGrad2.addColorStop(1, col.stroke);
          ctx.fillStyle = ballGrad2;
          ctx.fill();
          ctx.strokeStyle = col.stroke;
          ctx.lineWidth = 1.5;
          ctx.stroke();

          ctx.beginPath();
          ctx.arc(bx - 3, by - 3, 2.5, 0, Math.PI * 2);
          ctx.fillStyle = "rgba(255,255,255,0.5)";
          ctx.fill();

          ctx.font = "bold 8px Orbitron, sans-serif";
          ctx.fillStyle = "#fff";
          ctx.textAlign = "center";
          ctx.textBaseline = "middle";
          ctx.fillText(String(bIdx2 + 1), bx, by + 0.5);
        }

        ctx.restore();

        // ===== 날아가는 공은 클리핑 없이 최상단에 그리기 =====
        if (activeBallRef.current && isBallFlyingRef.current) {
          drawBall(activeBallRef.current, currentBallIndexRef.current);
        }

        rafRef.current = requestAnimationFrame(loop);
      };
      rafRef.current = requestAnimationFrame(loop);
    }, [
      cleanupPhysics,
      onBallSettled,
      onAllNumbersSelected,
      onLaunchEnd,
      spawnParticles,
      generateRandomBumperPositions,
      generateRandomDeflectorPositions,
      generateRandomSpinnerPositions,
      shuffleBasketNumbers,
    ]);

    // ===== useImperativeHandle: 모든 함수 정의 이후에 위치 =====
    useImperativeHandle(ref, () => ({
      reset: () => {
        cleanupPhysics();
        runnerRef.current.enabled = false;
        settledRef.current.clear();
        settledBallsRef.current = [];
        stableTimersRef.current.clear();
        particlesRef.current = [];
        bumperFlashRef.current.clear();
        trailsRef.current.clear();
        activeBallRef.current = null;
        basketSensorsRef.current = [];
        worldBodiesRef.current = [];
        bumpersRef.current = [];
        deflectorsRef.current = [];
        spinnersRef.current = [];
        spinnerAnglesRef.current = [];
        basketTopBumpersRef.current = [];
        isBallFlyingRef.current = false;
        plungerRef.current.pullAmount = 0;
        plungerRef.current.currentY = plungerRef.current.baseY;
        plungerRef.current.springAnim = 0;
        plungerRef.current.canLaunch = true;
        plungerRef.current.ballWaiting = true;
        gateWallRef.current = null;
        gateActiveRef.current = false;
        ballLaunchTimeRef.current = 0;
        basketEntryTimeRef.current = 0;
        hasEnteredBasketRef.current = false;
        blastOffHandledRef.current = false;
        blastOffRef.current.active = false;
        goodEffectRef.current.active = false;
        settledNumberDisplayRef.current.active = false;
        if (retryTimeoutRef.current) {
          clearTimeout(retryTimeoutRef.current);
          retryTimeoutRef.current = null;
        }
        generateRandomBumperPositions();
        generateRandomDeflectorPositions();
        generateRandomSpinnerPositions();
        // 리셋마다 항상 새로운 랜덤 번호 배치
        basketNumbersRef.current = [];
        shuffleBasketNumbers();
      },
      initWorld: () => {
        initPhysicsWorld();
      },
      prepareBall: () => {
        prepareBallForLaunch();
      },
    }));

    // Draw routine
    const drawFrame = useCallback(() => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext("2d");
      if (!ctx) return;

      const bumpers = bumpersRef.current;
      const deflectors = deflectorsRef.current;
      const spinners = spinnersRef.current;
      const plunger = plungerRef.current;
      const plungerX = GAME_W + PLUNGER_AREA_W / 2;

      ctx.clearRect(0, 0, TOTAL_W, GAME_H);

      // ----- Main playfield background -----
      ctx.save();
      ctx.beginPath();
      ctx.rect(0, 0, GAME_W, GAME_H);
      ctx.clip();
      const bgGrad = ctx.createRadialGradient(GAME_W / 2, GAME_H * 0.25, 50, GAME_W / 2, GAME_H * 0.25, GAME_W);
      bgGrad.addColorStop(0, "#fbbf24");
      bgGrad.addColorStop(0.35, "#f59e0b");
      bgGrad.addColorStop(0.65, "#d97706");
      bgGrad.addColorStop(1, "#78350f");
      ctx.fillStyle = bgGrad;
      ctx.fill();
      ctx.restore();

      // Top arch
      ctx.strokeStyle = "#92400e";
      ctx.lineWidth = 5;
      ctx.beginPath();
      ctx.arc(GAME_W / 2, 0, 240, 0.12 * Math.PI, 0.88 * Math.PI);
      ctx.stroke();
      ctx.strokeStyle = "#b45309";
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.arc(GAME_W / 2, 0, 236, 0.12 * Math.PI, 0.88 * Math.PI);
      ctx.stroke();

      // Bumpers
      bumpers.forEach((bumper: any, idx: number) => {
        const r = bumper.circleRadius || 32;
        const flash = bumperFlashRef.current.get(bumper.label) || 0;
        const bumperData = randomBumperPositionsRef.current[idx];
        const isBlue = bumperData?.type === "blue";

        if (flash > 0) {
          ctx.beginPath();
          ctx.arc(bumper.position.x, bumper.position.y, r + 12, 0, Math.PI * 2);
          ctx.fillStyle = isBlue
            ? `rgba(59, 130, 246, ${flash * 0.5})`
            : `rgba(239, 68, 68, ${flash * 0.5})`;
          ctx.fill();
          bumperFlashRef.current.set(bumper.label, Math.max(0, flash - 0.025));
        }

        ctx.beginPath();
        ctx.arc(bumper.position.x, bumper.position.y, r, 0, Math.PI * 2);
        const bGrad = ctx.createRadialGradient(
          bumper.position.x - r * 0.3,
          bumper.position.y - r * 0.3,
          r * 0.1,
          bumper.position.x,
          bumper.position.y,
          r
        );
        if (isBlue) {
          bGrad.addColorStop(0, "#60a5fa");
          bGrad.addColorStop(0.6, "#3b82f6");
          bGrad.addColorStop(1, "#1d4ed8");
        } else {
          bGrad.addColorStop(0, "#ff6b6b");
          bGrad.addColorStop(0.6, "#ef4444");
          bGrad.addColorStop(1, "#b91c1c");
        }
        ctx.fillStyle = bGrad;
        ctx.fill();
        ctx.strokeStyle = isBlue ? "#93c5fd" : "#fbbf24";
        ctx.lineWidth = 3;
        ctx.stroke();
        ctx.beginPath();
        ctx.arc(bumper.position.x, bumper.position.y, r * 0.5, 0, Math.PI * 2);
        ctx.strokeStyle = "rgba(255,255,255,0.35)";
        ctx.lineWidth = 2;
        ctx.stroke();
        ctx.font = "bold 14px Orbitron, sans-serif";
        ctx.fillStyle = "#fff";
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText("\u2605", bumper.position.x, bumper.position.y);
      });

      // Deflectors
      deflectors.forEach((d: any, idx: number) => {
        ctx.save();
        ctx.translate(d.position.x, d.position.y);
        ctx.rotate(d.angle);
        const dData = randomDeflectorPositionsRef.current[idx];
        const w = dData ? dData.w : 55;
        const h = 15;
        const dGrad = ctx.createLinearGradient(-w / 2, 0, w / 2, 0);
        dGrad.addColorStop(0, "#1e40af");
        dGrad.addColorStop(0.5, "#3b82f6");
        dGrad.addColorStop(1, "#1e40af");
        ctx.fillStyle = dGrad;
        ctx.beginPath();
        ctx.roundRect(-w / 2, -h / 2, w, h, 5);
        ctx.fill();
        ctx.strokeStyle = "#60a5fa";
        ctx.lineWidth = 1.5;
        ctx.stroke();
        ctx.restore();
      });

      // Spinners - 보라색 회전 막대
      spinners.forEach((s: any, idx: number) => {
        const flash = bumperFlashRef.current.get(s.label) || 0;
        const sData = randomSpinnerPositionsRef.current[idx];
        if (!sData) return;
        const angle = spinnerAnglesRef.current[idx] || 0;

        ctx.save();
        ctx.translate(s.position.x, s.position.y);

        // 플래시 글로우
        if (flash > 0) {
          ctx.beginPath();
          ctx.arc(0, 0, sData.w / 2 + 10, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(167, 139, 250, ${flash * 0.4})`;
          ctx.fill();
          bumperFlashRef.current.set(s.label, Math.max(0, flash - 0.025));
        }

        // 중앙 축
        ctx.beginPath();
        ctx.arc(0, 0, 6, 0, Math.PI * 2);
        const axleGrad = ctx.createRadialGradient(0, 0, 1, 0, 0, 6);
        axleGrad.addColorStop(0, "#e9d5ff");
        axleGrad.addColorStop(1, "#7c3aed");
        ctx.fillStyle = axleGrad;
        ctx.fill();
        ctx.strokeStyle = "#a78bfa";
        ctx.lineWidth = 1.5;
        ctx.stroke();

        // 회전 막대
        ctx.rotate(angle);
        const w = sData.w;
        const h2 = 8;
        const sGrad = ctx.createLinearGradient(-w / 2, 0, w / 2, 0);
        sGrad.addColorStop(0, "#7c3aed");
        sGrad.addColorStop(0.3, "#a78bfa");
        sGrad.addColorStop(0.5, "#c084fc");
        sGrad.addColorStop(0.7, "#a78bfa");
        sGrad.addColorStop(1, "#7c3aed");
        ctx.fillStyle = sGrad;
        ctx.beginPath();
        ctx.roundRect(-w / 2, -h2 / 2, w, h2, 4);
        ctx.fill();
        ctx.strokeStyle = "#c084fc";
        ctx.lineWidth = 1.5;
        ctx.stroke();

        // 양쪽 끝 원형 장식
        [-1, 1].forEach((dir) => {
          ctx.beginPath();
          ctx.arc((w / 2) * dir, 0, 5, 0, Math.PI * 2);
          ctx.fillStyle = "#c084fc";
          ctx.fill();
          ctx.strokeStyle = "#e9d5ff";
          ctx.lineWidth = 1;
          ctx.stroke();
        });
        ctx.restore();
      });

      // Basket area (5줄 × 9칸, 1~45 랜덤 배치)
      const basketStartX = 15;
      const basketStartY = 600;
      const basketW = (GAME_W - 30) / BASKET_COLS;
      const basketH = 36;

      ctx.fillStyle = "rgba(0,0,0,0.3)";
      ctx.beginPath();
      ctx.roundRect(basketStartX - 5, basketStartY - 5, BASKET_COLS * basketW + 10, BASKET_ROWS * basketH + 10, 10);
      ctx.fill();

      const idleBasketNums = basketNumbersRef.current.length > 0 ? basketNumbersRef.current : Array.from({ length: 45 }, (_, i) => i + 1);

      for (let row = 0; row < BASKET_ROWS; row++) {
        for (let col = 0; col < BASKET_COLS; col++) {
          const idx = row * BASKET_COLS + col;
          if (idx >= 45) break;
          const num = idleBasketNums[idx];
          const x = basketStartX + col * basketW;
          const y = basketStartY + row * basketH;
          ctx.beginPath();
          ctx.roundRect(x + 1, y + 1, basketW - 2, basketH - 2, 4);
          const cellGrad = ctx.createLinearGradient(x, y, x, y + basketH);
          cellGrad.addColorStop(0, "rgba(255,255,255,0.12)");
          cellGrad.addColorStop(1, "rgba(255,255,255,0.04)");
          ctx.fillStyle = cellGrad;
          ctx.fill();
          ctx.strokeStyle = "rgba(255,255,255,0.15)";
          ctx.lineWidth = 1;
          ctx.stroke();

          ctx.font = "bold 14px Orbitron, sans-serif";
          ctx.fillStyle = "rgba(255,255,255,0.75)";
          ctx.textAlign = "center";
          ctx.textBaseline = "middle";
          ctx.fillText(String(num), x + basketW / 2, y + basketH / 2);
        }
      }

      // ===== Plunger area =====
      ctx.save();
      ctx.beginPath();
      ctx.rect(GAME_W, 0, PLUNGER_AREA_W, GAME_H);
      ctx.clip();

      const laneBg = ctx.createLinearGradient(GAME_W, 0, TOTAL_W, 0);
      laneBg.addColorStop(0, "#4a2c0a");
      laneBg.addColorStop(0.5, "#5c3610");
      laneBg.addColorStop(1, "#4a2c0a");
      ctx.fillStyle = laneBg;
      ctx.fillRect(GAME_W, 0, PLUNGER_AREA_W, GAME_H);

      // 플런저 레인 왼쪽 경계선
      ctx.strokeStyle = "#92400e";
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.moveTo(GAME_W, 500);
      ctx.lineTo(GAME_W, GAME_H);
      ctx.stroke();

      // 레인 장식 줄무늬
      ctx.strokeStyle = "rgba(255,255,255,0.05)";
      ctx.lineWidth = 1;
      for (let gy = 80; gy < GAME_H - 50; gy += 20) {
        ctx.beginPath();
        ctx.moveTo(GAME_W + 8, gy);
        ctx.lineTo(GAME_W + PLUNGER_AREA_W - 8, gy);
        ctx.stroke();
      }

      if (gateActiveRef.current) {
        ctx.strokeStyle = "#ef4444";
        ctx.lineWidth = 4;
        ctx.shadowColor = "#ef4444";
        ctx.shadowBlur = 8;
        ctx.beginPath();
        ctx.moveTo(GAME_W, 0);
        ctx.lineTo(GAME_W, GAME_H);
        ctx.stroke();
        ctx.shadowBlur = 0;
      } else {
        ctx.strokeStyle = "rgba(146, 64, 14, 0.4)";
        ctx.lineWidth = 2;
        ctx.setLineDash([6, 6]);
        ctx.beginPath();
        ctx.moveTo(GAME_W, 0);
        ctx.lineTo(GAME_W, 700);
        ctx.stroke();
        ctx.setLineDash([]);
      }

      // 플런저 스프링 그리기
      const coilCount = 8;
      const coilSpacing = (GAME_H - 120) / coilCount;

      ctx.strokeStyle = "#9ca3af";
      ctx.lineWidth = 3;
      ctx.beginPath();
      for (let c = 0; c <= coilCount; c++) {
        const cy = 60 + c * coilSpacing;
        const cx = c % 2 === 0 ? plungerX - 10 : plungerX + 10;
        if (c === 0) ctx.moveTo(cx, cy);
        else ctx.lineTo(cx, cy);
      }
      ctx.stroke();

      // 핸들 본체 (idle에서 누락되어 있던 부분 추가)
      const idleHandleW = 36;
      const idleHandleGrad = ctx.createLinearGradient(plungerX - idleHandleW / 2, plunger.baseY, plungerX + idleHandleW / 2, plunger.baseY);
      idleHandleGrad.addColorStop(0, "#dc2626");
      idleHandleGrad.addColorStop(0.3, "#ef4444");
      idleHandleGrad.addColorStop(0.5, "#f87171");
      idleHandleGrad.addColorStop(0.7, "#ef4444");
      idleHandleGrad.addColorStop(1, "#dc2626");
      ctx.fillStyle = idleHandleGrad;
      ctx.beginPath();
      ctx.roundRect(plungerX - idleHandleW / 2, plunger.baseY, idleHandleW, 50, 8);
      ctx.fill();
      ctx.strokeStyle = "#991b1b";
      ctx.lineWidth = 2;
      ctx.stroke();

      ctx.strokeStyle = "rgba(255,255,255,0.2)";
      ctx.lineWidth = 1.5;
      for (let gl = 0; gl < 3; gl++) {
        const gy = plunger.baseY + 15 + gl * 10;
        ctx.beginPath();
        ctx.moveTo(plungerX - 10, gy);
        ctx.lineTo(plungerX + 10, gy);
        ctx.stroke();
      }

      // 핸들 상단 캡
      const capGrad = ctx.createRadialGradient(plungerX, plunger.baseY, 2, plungerX, plunger.baseY, 18);
      capGrad.addColorStop(0, "#fca5a5");
      capGrad.addColorStop(1, "#b91c1c");
      ctx.fillStyle = capGrad;
      ctx.beginPath();
      ctx.arc(plungerX, plunger.baseY, 16, Math.PI, 0);
      ctx.fill();
      ctx.strokeStyle = "#991b1b";
      ctx.lineWidth = 2;
      ctx.stroke();

      // Power meter
      if (plungerRef.current.pullAmount > 0.05) {
        const powerH = 100;
        const powerY = plungerRef.current.baseY - 180;
        const powerX = GAME_W + 8;
        const powerW = 8;

        ctx.fillStyle = "rgba(0,0,0,0.4)";
        ctx.beginPath();
        ctx.roundRect(powerX, powerY, powerW, powerH, 4);
        ctx.fill();

        const fillH = powerH * plungerRef.current.pullAmount;
        const powerGrad = ctx.createLinearGradient(powerX, powerY + powerH, powerX, powerY);
        powerGrad.addColorStop(0, "#22c55e");
        powerGrad.addColorStop(0.5, "#eab308");
        powerGrad.addColorStop(1, "#ef4444");
        ctx.fillStyle = powerGrad;
        ctx.beginPath();
        ctx.roundRect(powerX, powerY + powerH - fillH, powerW, fillH, 4);
        ctx.fill();
      }

      // Ball indicators
      for (let i = plungerRef.current.canLaunch ? currentBallIndexRef.current : 0; i < BALL_COUNT; i++) {
        const col = BALL_COLORS[i];
        const indicatorY = GAME_H - 30 - (i - currentBallIndexRef.current) * 28;
        ctx.beginPath();
        ctx.arc(plungerX, indicatorY, 8, 0, Math.PI * 2);
        ctx.fillStyle = i === currentBallIndexRef.current ? col.fill : col.fill + "60";
        ctx.fill();
        ctx.strokeStyle = i === currentBallIndexRef.current ? col.stroke : col.stroke + "40";
        ctx.lineWidth = 1.5;
        ctx.stroke();
        ctx.font = "bold 7px Orbitron, sans-serif";
        ctx.fillStyle = "#fff";
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.fillText(String(i + 1), plungerX, indicatorY);
      }

      // Prompt when ready to launch
      if (plungerRef.current.canLaunch && plungerRef.current.ballWaiting && !isCompleteRef.current) {
        ctx.save();
        const t = Date.now() * 0.004;
        const bounceY = Math.sin(t) * 4;
        const pulseAlpha = 0.6 + Math.sin(t * 1.2) * 0.35;

        // handleY 정의 추가
        const pullOffset = plunger.pullAmount * plunger.maxPull * 0.3;
        const handleY = plungerRef.current.baseY + pullOffset;

        // 핸들에서 90px 위에 안내 표시
        const guideY = handleY - 90 + bounceY;

        // 글로우 배경
        ctx.beginPath();
        ctx.arc(plungerX, guideY + 10, 28, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(251, 191, 36, ${pulseAlpha * 0.2})`;
        ctx.fill();

        // 아래 방향 화살표 (애니메이션)
        ctx.globalAlpha = pulseAlpha;
        ctx.fillStyle = "#fbbf24";
        ctx.beginPath();
        ctx.moveTo(plungerX - 12, guideY);
        ctx.lineTo(plungerX + 12, guideY);
        ctx.lineTo(plungerX, guideY + 16);
        ctx.closePath();
        ctx.fill();

        // 두번째 화살표 (약간 아래, 더 투명)
        ctx.globalAlpha = pulseAlpha * 0.5;
        ctx.beginPath();
        ctx.moveTo(plungerX - 10, guideY + 18);
        ctx.lineTo(plungerX + 10, guideY + 18);
        ctx.lineTo(plungerX, guideY + 30);
        ctx.closePath();
        ctx.fill();

        // "PULL" 텍스트 (화살표 위 - 더 위로)
        ctx.globalAlpha = pulseAlpha;
        ctx.font = "bold 11px Orbitron, sans-serif";
        ctx.fillStyle = "#fbbf24";
        ctx.textAlign = "center";
        ctx.textBaseline = "middle";
        ctx.shadowColor = "rgba(251, 191, 36, 0.6)";
        ctx.shadowBlur = 6;
        ctx.fillText("PULL \u25BC", plungerX, guideY - 28);
        ctx.shadowBlur = 0;

        ctx.restore();
      }

      ctx.restore();
    }, []);

    // Mouse / touch handling for the plunger
    useEffect(() => {
      const canvas = canvasRef.current;
      if (!canvas) return;

      const getCanvasPos = (clientX: number, clientY: number) => {
        const rect = canvas.getBoundingClientRect();
        const scaleX = TOTAL_W / rect.width;
        const scaleY = GAME_H / rect.height;
        return {
          x: (clientX - rect.left) * scaleX,
          y: (clientY - rect.top) * scaleY,
        };
      };

      const isInPlungerArea = (x: number, y: number) => {
        const plunger = plungerRef.current;
        return (
          x > GAME_W - 10 &&
          x < TOTAL_W + 10 &&
          y > plunger.baseY - 100 &&
          y < plunger.baseY + plunger.maxPull + 80
        );
      };

      const handleDown = (clientX: number, clientY: number, button?: number) => {
        if (button !== undefined && button !== 0) return;
        const pos = getCanvasPos(clientX, clientY);
        if (isInPlungerArea(pos.x, pos.y) && plungerRef.current.canLaunch && plungerRef.current.ballWaiting) {
          plungerRef.current.isDragging = true;
        }
      };

      const handleMove = (clientX: number, clientY: number) => {
        if (!plungerRef.current.isDragging) return;
        const pos = getCanvasPos(clientX, clientY);
        const plunger = plungerRef.current;
        const pull = Math.max(0, Math.min(1, (pos.y - plunger.baseY) / plunger.maxPull));
        plungerRef.current.pullAmount = pull;
        plungerRef.current.currentY = plungerRef.current.baseY + pull * plungerRef.current.maxPull * 0.3;
      };

      const handleUp = () => {
        if (plungerRef.current.isDragging) {
          if (plungerRef.current.pullAmount > 0.02) {
            launchCurrentBall();
          }
          plungerRef.current.isDragging = false;
          plungerRef.current.pullAmount = 0;
          plungerRef.current.currentY = plungerRef.current.baseY;
        }
      };

      const onMouseDown = (e: MouseEvent) => {
        e.preventDefault();
        handleDown(e.clientX, e.clientY, e.button);
      };
      const onMouseMove = (e: MouseEvent) => handleMove(e.clientX, e.clientY);
      const onMouseUp = (e: MouseEvent) => {
        if (e.button === 0) handleUp();
      };
      const onTouchStart = (e: TouchEvent) => {
        if (e.touches.length > 0) {
          handleDown(e.touches[0].clientX, e.touches[0].clientY);
        }
      };
      const onTouchMove = (e: TouchEvent) => {
        if (e.touches.length > 0) {
          e.preventDefault();
          handleMove(e.touches[0].clientX, e.touches[0].clientY);
        }
      };
      const onTouchEnd = () => handleUp();

      const onContextMenu = (e: Event) => e.preventDefault();

      canvas.addEventListener("mousedown", onMouseDown);
      window.addEventListener("mousemove", onMouseMove);
      window.addEventListener("mouseup", onMouseUp);
      canvas.addEventListener("touchstart", onTouchStart, { passive: false });
      canvas.addEventListener("touchmove", onTouchMove, { passive: false });
      canvas.addEventListener("touchend", onTouchEnd);
      canvas.addEventListener("contextmenu", onContextMenu);

      return () => {
        canvas.removeEventListener("mousedown", onMouseDown);
        window.removeEventListener("mousemove", onMouseMove);
        window.removeEventListener("mouseup", onMouseUp);
        canvas.removeEventListener("touchstart", onTouchStart);
        canvas.removeEventListener("touchmove", onTouchMove);
        canvas.removeEventListener("touchend", onTouchEnd);
        canvas.removeEventListener("contextmenu", onContextMenu);
      };
    }, [launchCurrentBall]);

    // Idle animation (when the physics engine isn't running)
    useEffect(() => {
      if (runnerRef.current.enabled) return;
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext("2d");
      if (!ctx) return;

      if (randomBumperPositionsRef.current.length === 0) generateRandomBumperPositions();
      if (randomDeflectorPositionsRef.current.length === 0) generateRandomDeflectorPositions();
      if (randomSpinnerPositionsRef.current.length === 0) generateRandomSpinnerPositions();

      let idleSpinnerAngles = randomSpinnerPositionsRef.current.map((_, i) => spinnerAnglesRef.current[i] || 0);

      const drawIdle = () => {
        ctx.clearRect(0, 0, TOTAL_W, GAME_H);

        ctx.save();
        ctx.beginPath();
        ctx.rect(0, 0, GAME_W, GAME_H);
        ctx.clip();
        const bgGrad = ctx.createRadialGradient(GAME_W / 2, GAME_H * 0.25, 50, GAME_W / 2, GAME_H * 0.25, GAME_W);
        bgGrad.addColorStop(0, "#fbbf24");
        bgGrad.addColorStop(0.35, "#f59e0b");
        bgGrad.addColorStop(0.65, "#d97706");
        bgGrad.addColorStop(1, "#78350f");
        ctx.fillStyle = bgGrad;
        ctx.fill();
        ctx.restore();

        ctx.strokeStyle = "#92400e";
        ctx.lineWidth = 5;
        ctx.beginPath();
        ctx.arc(GAME_W / 2, 0, 240, 0.12 * Math.PI, 0.88 * Math.PI);
        ctx.stroke();

        // Bumpers (idle)
        randomBumperPositionsRef.current.forEach((b) => {
          const isBlue = b.type === "blue";
          ctx.beginPath();
          ctx.arc(b.x, b.y, b.r, 0, Math.PI * 2);
          const bGrad = ctx.createRadialGradient(b.x - b.r * 0.3, b.y - b.r * 0.3, b.r * 0.1, b.x, b.y, b.r);
          if (isBlue) {
            bGrad.addColorStop(0, "#60a5fa");
            bGrad.addColorStop(0.6, "#3b82f6");
            bGrad.addColorStop(1, "#1d4ed8");
          } else {
            bGrad.addColorStop(0, "#ff6b6b");
            bGrad.addColorStop(0.6, "#ef4444");
            bGrad.addColorStop(1, "#b91c1c");
          }
          ctx.fillStyle = bGrad;
          ctx.fill();
          ctx.strokeStyle = isBlue ? "#93c5fd" : "#fbbf24";
          ctx.lineWidth = 3;
          ctx.stroke();
          ctx.beginPath();
          ctx.arc(b.x, b.y, b.r * 0.5, 0, Math.PI * 2);
          ctx.strokeStyle = "rgba(255,255,255,0.35)";
          ctx.lineWidth = 2;
          ctx.stroke();
          ctx.font = "bold 14px Orbitron, sans-serif";
          ctx.fillStyle = "#fff";
          ctx.textAlign = "center";
          ctx.textBaseline = "middle";
          ctx.fillText("\u2605", b.x, b.y);
        });

        // Deflectors (idle) - 3개
        randomDeflectorPositionsRef.current.forEach((d) => {
          ctx.save();
          ctx.translate(d.x, d.y);
          ctx.rotate(d.angle);
          const dGrad = ctx.createLinearGradient(-d.w / 2, 0, d.w / 2, 0);
          dGrad.addColorStop(0, "#1e40af");
          dGrad.addColorStop(0.5, "#3b82f6");
          dGrad.addColorStop(1, "#1e40af");
          ctx.fillStyle = dGrad;
          ctx.beginPath();
          ctx.roundRect(-d.w / 2, -7.5, d.w, 15, 5);
          ctx.fill();
          ctx.strokeStyle = "#60a5fa";
          ctx.lineWidth = 1.5;
          ctx.stroke();
          ctx.restore();
        });

        // Spinners (idle) - 2개 보라색 회전 막대
        randomSpinnerPositionsRef.current.forEach((s, idx) => {
          idleSpinnerAngles[idx] += idx === 1 ? -0.03 : 0.03; // 인덱스 1(오른쪽 중간)만 반시계

          ctx.save();
          ctx.translate(s.x, s.y);

          // 플래시 글로우
          if (bumperFlashRef.current.get(s.label) || 0 > 0) {
            ctx.beginPath();
            ctx.arc(0, 0, s.w / 2 + 10, 0, Math.PI * 2);
            ctx.fillStyle = `rgba(167, 139, 250, ${(bumperFlashRef.current.get(s.label) || 0) * 0.4})`;
            ctx.fill();
            bumperFlashRef.current.set(s.label, Math.max(0, (bumperFlashRef.current.get(s.label) || 0) - 0.025));
          }

          // 중앙 축
          ctx.beginPath();
          ctx.arc(0, 0, 6, 0, Math.PI * 2);
          const axleGrad = ctx.createRadialGradient(0, 0, 1, 0, 0, 6);
          axleGrad.addColorStop(0, "#e9d5ff");
          axleGrad.addColorStop(1, "#7c3aed");
          ctx.fillStyle = axleGrad;
          ctx.fill();
          ctx.strokeStyle = "#a78bfa";
          ctx.lineWidth = 1.5;
          ctx.stroke();

          // 회전 막대
          ctx.rotate(idleSpinnerAngles[idx]);
          const w = s.w;
          const h2 = 8;
          const sGrad = ctx.createLinearGradient(-w / 2, 0, w / 2, 0);
          sGrad.addColorStop(0, "#7c3aed");
          sGrad.addColorStop(0.3, "#a78bfa");
          sGrad.addColorStop(0.5, "#c084fc");
          sGrad.addColorStop(0.7, "#a78bfa");
          sGrad.addColorStop(1, "#7c3aed");
          ctx.fillStyle = sGrad;
          ctx.beginPath();
          ctx.roundRect(-w / 2, -h2 / 2, w, h2, 4);
          ctx.fill();
          ctx.strokeStyle = "#c084fc";
          ctx.lineWidth = 1.5;
          ctx.stroke();

          // 양쪽 끝 원형 장식
          [-1, 1].forEach((dir) => {
            ctx.beginPath();
            ctx.arc((w / 2) * dir, 0, 5, 0, Math.PI * 2);
            ctx.fillStyle = "#c084fc";
            ctx.fill();
            ctx.strokeStyle = "#e9d5ff";
            ctx.lineWidth = 1;
            ctx.stroke();
          });

          ctx.restore();
        });

        // Baskets (idle) - 5줄 × 9칸, 1~45 랜덤 배치
        const basketStartX = 15;
        const basketStartY = 600;
        const basketW = (GAME_W - 30) / BASKET_COLS;
        const basketH = 36;

        ctx.fillStyle = "rgba(0,0,0,0.3)";
        ctx.beginPath();
        ctx.roundRect(basketStartX - 5, basketStartY - 5, BASKET_COLS * basketW + 10, BASKET_ROWS * basketH + 10, 10);
        ctx.fill();

        const idleBasketNums = basketNumbersRef.current.length > 0 ? basketNumbersRef.current : Array.from({ length: 45 }, (_, i) => i + 1);

        for (let row = 0; row < BASKET_ROWS; row++) {
          for (let col = 0; col < BASKET_COLS; col++) {
            const idx = row * BASKET_COLS + col;
            if (idx >= 45) break;
            const num = idleBasketNums[idx];
            const x = basketStartX + col * basketW;
            const y = basketStartY + row * basketH;
            ctx.beginPath();
            ctx.roundRect(x + 1, y + 1, basketW - 2, basketH - 2, 4);
            const cellGrad = ctx.createLinearGradient(x, y, x, y + basketH);
            cellGrad.addColorStop(0, "rgba(255,255,255,0.12)");
            cellGrad.addColorStop(1, "rgba(255,255,255,0.04)");
            ctx.fillStyle = cellGrad;
            ctx.fill();
            ctx.strokeStyle = "rgba(255,255,255,0.15)";
            ctx.lineWidth = 1;
            ctx.stroke();

            ctx.font = "bold 14px Orbitron, sans-serif";
            ctx.fillStyle = "rgba(255,255,255,0.75)";
            ctx.textAlign = "center";
            ctx.textBaseline = "middle";
            ctx.fillText(String(num), x + basketW / 2, y + basketH / 2);
          }
        }

        ctx.restore();

        // Plunger area (idle)
        ctx.save();
        ctx.beginPath();
        ctx.rect(GAME_W, 0, PLUNGER_AREA_W, GAME_H);
        ctx.clip();

        const laneBg = ctx.createLinearGradient(GAME_W, 0, TOTAL_W, 0);
        laneBg.addColorStop(0, "#4a2c0a");
        laneBg.addColorStop(0.5, "#5c3610");
        laneBg.addColorStop(1, "#4a2c0a");
        ctx.fillStyle = laneBg;
        ctx.fillRect(GAME_W, 0, PLUNGER_AREA_W, GAME_H);

        ctx.strokeStyle = "#92400e";
        ctx.lineWidth = 3;
        ctx.beginPath();
        ctx.moveTo(GAME_W, 500);
        ctx.lineTo(GAME_W, GAME_H);
        ctx.stroke();

        ctx.strokeStyle = "rgba(255,255,255,0.05)";
        ctx.lineWidth = 1;
        for (let gy = 80; gy < GAME_H - 50; gy += 20) {
          ctx.beginPath();
          ctx.moveTo(GAME_W + 8, gy);
          ctx.lineTo(GAME_W + PLUNGER_AREA_W - 8, gy);
          ctx.stroke();
        }

        const plungerX = GAME_W + PLUNGER_AREA_W / 2;
        const plunger = plungerRef.current;

        const springTop = plunger.baseY - 60;
        const springBottom = plunger.baseY;
        const coilCount = 8;
        const coilSpacing = (springBottom - springTop) / coilCount;

        ctx.strokeStyle = "#9ca3af";
        ctx.lineWidth = 3;
        ctx.beginPath();
        for (let c = 0; c <= coilCount; c++) {
          const cy = springTop + c * coilSpacing;
          const cx = c % 2 === 0 ? plungerX - 10 : plungerX + 10;
          if (c === 0) ctx.moveTo(cx, cy);
          else ctx.lineTo(cx, cy);
        }
        ctx.stroke();

        // 핸들 본체 (idle에서 누락되어 있던 부분 추가)
        const idleHandleW = 36;
        const idleHandleGrad = ctx.createLinearGradient(plungerX - idleHandleW / 2, plunger.baseY, plungerX + idleHandleW / 2, plunger.baseY);
        idleHandleGrad.addColorStop(0, "#dc2626");
        idleHandleGrad.addColorStop(0.3, "#ef4444");
        idleHandleGrad.addColorStop(0.5, "#f87171");
        idleHandleGrad.addColorStop(0.7, "#ef4444");
        idleHandleGrad.addColorStop(1, "#dc2626");
        ctx.fillStyle = idleHandleGrad;
        ctx.beginPath();
        ctx.roundRect(plungerX - idleHandleW / 2, plunger.baseY, idleHandleW, 50, 8);
        ctx.fill();
        ctx.strokeStyle = "#991b1b";
        ctx.lineWidth = 2;
        ctx.stroke();

        ctx.strokeStyle = "rgba(255,255,255,0.2)";
        ctx.lineWidth = 1.5;
        for (let gl = 0; gl < 3; gl++) {
          const gy = plunger.baseY + 15 + gl * 10;
          ctx.beginPath();
          ctx.moveTo(plungerX - 10, gy);
          ctx.lineTo(plungerX + 10, gy);
          ctx.stroke();
        }

        const capGrad = ctx.createRadialGradient(plungerX, plunger.baseY, 2, plungerX, plunger.baseY, 18);
        capGrad.addColorStop(0, "#fca5a5");
        capGrad.addColorStop(1, "#b91c1c");
        ctx.fillStyle = capGrad;
        ctx.beginPath();
        ctx.arc(plungerX, plunger.baseY, 16, Math.PI, 0);
        ctx.fill();
        ctx.strokeStyle = "#991b1b";
        ctx.lineWidth = 2;
        ctx.stroke();

        for (let i = 0; i < BALL_COUNT; i++) {
          const col = BALL_COLORS[i];
          const indicatorY = GAME_H - 30 - (i - currentBallIndexRef.current) * 28;
          ctx.beginPath();
          ctx.arc(plungerX, indicatorY, 8, 0, Math.PI * 2);
          ctx.fillStyle = col.fill;
          ctx.fill();
          ctx.strokeStyle = col.stroke;
          ctx.lineWidth = 1.5;
          ctx.stroke();
          ctx.font = "bold 7px Orbitron, sans-serif";
          ctx.fillStyle = "#fff";
          ctx.textAlign = "center";
          ctx.textBaseline = "middle";
          ctx.fillText(String(i + 1), plungerX, indicatorY);
        }

        // Prompt when ready to launch (idle) - 게임 시작 후에만 표시
        if (isStarted && plungerRef.current.canLaunch && plungerRef.current.ballWaiting && !isCompleteRef.current) {
          ctx.save();
          const t = Date.now() * 0.004;
          const bounceY = Math.sin(t) * 4;
          const pulseAlpha = 0.6 + Math.sin(t * 1.2) * 0.35;

          const guideY = plunger.baseY - 130 + bounceY;

          ctx.beginPath();
          ctx.arc(plungerX, guideY + 10, 28, 0, Math.PI * 2);
          ctx.fillStyle = `rgba(251, 191, 36, ${pulseAlpha * 0.2})`;
          ctx.fill();

          ctx.globalAlpha = pulseAlpha;
          ctx.fillStyle = "#fbbf24";
          ctx.beginPath();
          ctx.moveTo(plungerX - 12, guideY);
          ctx.lineTo(plungerX + 12, guideY);
          ctx.lineTo(plungerX, guideY + 16);
          ctx.closePath();
          ctx.fill();

          ctx.globalAlpha = pulseAlpha * 0.5;
          ctx.beginPath();
          ctx.moveTo(plungerX - 10, guideY + 18);
          ctx.lineTo(plungerX + 10, guideY + 18);
          ctx.lineTo(plungerX, guideY + 30);
          ctx.closePath();
          ctx.fill();

          ctx.globalAlpha = pulseAlpha;
          ctx.font = "bold 11px Orbitron, sans-serif";
          ctx.fillStyle = "#fbbf24";
          ctx.textAlign = "center";
          ctx.textBaseline = "middle";
          ctx.shadowColor = "rgba(251, 191, 36, 0.6)";
          ctx.shadowBlur = 6;
          ctx.fillText("PULL \u25BC", plungerX, guideY - 28);
          ctx.shadowBlur = 0;

          ctx.restore();
        }

        ctx.restore();
      };

      drawIdle();
      const idleInterval = setInterval(drawIdle, 50);
      return () => clearInterval(idleInterval);
    }, [
      currentBallIndex,
      isComplete,
      isStarted,
      generateRandomBumperPositions,
      generateRandomDeflectorPositions,
      generateRandomSpinnerPositions,
      shuffleBasketNumbers,
    ]);

    // Cleanup on unmount
    useEffect(() => {
      return () => {
        cleanupPhysics();
      };
    }, [cleanupPhysics]);

    return (
      <canvas
        ref={canvasRef}
        width={TOTAL_W}
        height={GAME_H}
        className="w-full h-auto rounded-xl border-4 border-amber-700/60 shadow-2xl shadow-amber-900/30 cursor-pointer"
        style={{ touchAction: "none" }}
      />
    );
  }
);

PinballGame.displayName = "PinballGame";
export default PinballGame;
